import { supabase } from './supabaseClient';
import { Customer, CustomerChange, CustomerContact, CustomerPrice, DEFAULT_QUOTE_SECTIONS, FrameworkContract, Quote, QuoteFollowUpEntry, QuoteItem, QuoteDetails, QuoteTemplate, TextTemplate, TenderDocument, TenderDocumentKind, UserProfile } from '../types';
import { appendChangeEntry, diffCustomerChanges } from './customerChangeLog';
import { canTransition, quoteToReport, calculateQuoteTotals, computePosNumbers, typeDigitFromDocumentNumber } from './quoteLogic';
import { mergeQuoteUpdate, unionFollowUpLog } from './quoteMerge';
import { nextDocumentNumber, TypeDigit } from './documentNumberService';
import { AcceptQuoteValues, typzifferFuerProjektTyp } from './quoteAcceptDefaults';
import { buildFollowUpRecord, FOLLOW_UP_STATUSES, FollowUpInput, mergeFollowUpLog } from './quoteFollowUp';
import { CustomerStats, foldMergedRevenue } from './customerStats';
import { describeError } from './errorMessage';
import { ladeAlleSeiten } from './seitenweise';
import { rangeForName } from './customerNumberRanges';
import { createNotificationsForMentions, saveProjectToCloud } from './cloudService';
import { fixiereAuftragswertBestEffort } from './erpSteeringService';
import { applyRepresentatives } from './projectRepresentatives';
import { planungsEintraege, planungsPersonenAusIds } from './planungsZuweisung';
import {
  buildTranche,
  markTrancheApplied,
  normalizeOrderLines,
  openOrderLines,
  projectItemsForLines,
  statusAfterLines,
  trancheNoteText,
  validateOrderLines,
} from './quoteOrderTranches';
import { isMissingOrderTranchesSchema, MISSING_ORDER_TRANCHES_MESSAGE } from './quoteOrderSchema';
import {
  AcceptFrameworkValues,
  addFrameworkContract,
  buildFrameworkContractFromQuote,
  frameworkItemsFromQuote,
  isUnitPriceQuote,
  mergeFrameworkContractsOnSave,
} from './frameworkContractLogic';

// CRM + Angebotsmodul (Phase 1). CRUD-Stil wie cloudService.ts:
// async, throw bei error. snake_case<->camelCase-Mapping in den Helfern unten,
// damit der Rest der App ausschliesslich mit camelCase-Typen arbeitet.

// ─── Row-Mapper ──────────────────────────────────────────────────────────────

// Skonto nur, wenn BEIDE Werte tragen — ein halber Eintrag (Prozent ohne
// Frist oder umgekehrt) ist keine anwendbare Kondition.
const mapSkonto = (percentRaw: unknown, daysRaw: unknown): Customer['skonto'] => {
  const percent = Number(percentRaw);
  const days = Number(daysRaw);
  return percent > 0 && days > 0 ? { percent, days } : undefined;
};

const mapCustomerRow = (row: any): Customer => ({
  id: row.id,
  customerNumber: row.customer_number ?? undefined,
  customerType: row.customer_type === 'person' ? 'person' : row.customer_type ? 'company' : undefined,
  salutation: row.salutation ?? undefined,
  title: row.title ?? undefined,
  firstName: row.first_name ?? undefined,
  lastName: row.last_name ?? undefined,
  name: row.name,
  name2: row.name_2 ?? undefined,
  name3: row.name_3 ?? undefined,
  address: row.address ?? undefined,
  street: row.street ?? undefined,
  zip: row.zip ?? undefined,
  city: row.city ?? undefined,
  email: row.email ?? undefined,
  phone: row.phone ?? undefined,
  website: row.website ?? undefined,
  contacts: Array.isArray(row.contacts) ? row.contacts : [],
  frameworkContracts: Array.isArray(row.framework_contracts) ? row.framework_contracts : [],
  // NULL = globale Grundeinstellung, nicht 30: eine Zahl an dieser Stelle
  // waere eine erfundene Kondition und schluege in createInvoiceDraft die
  // Konfiguration des Rechnungsmoduls.
  paymentTermsDays: row.payment_terms_days ?? null,
  defaultDiscountPercent: Number(row.default_discount_percent ?? 0),
  skonto: mapSkonto(row.skonto_percent, row.skonto_days),
  ratingZahlungsmoral: row.rating_zahlungsmoral ?? undefined,
  ratingZusammenarbeit: row.rating_zusammenarbeit ?? undefined,
  ratingWirtschaftlichkeit: row.rating_wirtschaftlichkeit ?? undefined,
  ratingOverall: row.rating_overall ?? undefined,
  ratingNotes: row.rating_notes ?? undefined,
  notes: row.notes ?? undefined,
  branche: row.branche ?? undefined,
  lat: row.lat != null ? Number(row.lat) : undefined,
  lng: row.lng != null ? Number(row.lng) : undefined,
  geoQuality: row.geo_quality ?? undefined,
  geoManual: !!row.geo_manual,
  changeLog: Array.isArray(row.change_log) ? row.change_log : [],
  archivedAt: row.archived_at ?? undefined,
  archivedBy: row.archived_by ?? undefined,
  mergedIntoId: row.merged_into_id ?? undefined,
  createdBy: row.created_by,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapCustomerPriceRow = (row: any): CustomerPrice => ({
  id: row.id,
  customerId: row.customer_id,
  catalogItemId: row.catalog_item_id,
  unitPrice: row.unit_price != null ? Number(row.unit_price) : undefined,
  discountPercent: row.discount_percent != null ? Number(row.discount_percent) : undefined,
  note: row.note ?? undefined,
});

export const mapQuoteRow = (row: any): Quote => ({
  id: row.id,
  quoteNumber: row.quote_number,
  orderNumber: row.order_number ?? undefined,
  customerId: row.customer_id,
  customerName: row.customers?.name ?? undefined,
  projectId: row.project_id ?? undefined,
  status: row.status,
  title: row.title ?? '',
  quoteDate: row.quote_date,
  validUntil: row.valid_until ?? undefined,
  items: Array.isArray(row.items) ? (row.items as QuoteItem[]) : [],
  discountPercent: Number(row.discount_percent ?? 0),
  taxRate: Number(row.tax_rate ?? 0),
  netTotal: Number(row.net_total ?? 0),
  grossTotal: Number(row.gross_total ?? 0),
  introText: row.intro_text ?? undefined,
  outroText: row.outro_text ?? undefined,
  paymentTerms: row.payment_terms ?? undefined,
  pdfUrl: row.pdf_url ?? undefined,
  sentAt: row.sent_at ?? undefined,
  sentTo: row.sent_to ?? undefined,
  customerSnapshot: row.customer_snapshot ?? undefined,
  acceptedAt: row.accepted_at ?? undefined,
  rejectedAt: row.rejected_at ?? undefined,
  followUpDate: row.follow_up_date ?? undefined,
  followUpDoneAt: row.follow_up_done_at ?? undefined,
  followUpNotifiedAt: row.follow_up_notified_at ?? undefined,
  details: (row.details ?? {}) as QuoteDetails,
  // Bewusst NICHT in toQuoteRow: Auftragsteile schreiben nur acceptQuote und
  // quoteOrderService — ein Editor-Speichern aus aelterem Stand naehme sonst
  // einen Teil zurueck. Vor Migration Nr. 140 fehlt die Spalte: dann leer.
  orderTranches: Array.isArray(row.order_tranches) ? row.order_tranches : [],
  createdBy: row.created_by,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapQuoteTemplateRow = (row: any): QuoteTemplate => ({
  id: row.id,
  name: row.name,
  payload: (row.payload ?? {}) as QuoteTemplate['payload'],
  createdBy: row.created_by ?? undefined,
  createdAt: row.created_at ?? undefined,
});

// Nur definierte Felder in die Row uebernehmen, damit updateCustomer/updateQuote
// mit Partial<> keine Spalten versehentlich auf null setzen.
const toCustomerRow = (c: Partial<Customer>): Record<string, any> => {
  const row: Record<string, any> = {};
  if (c.customerNumber !== undefined) row.customer_number = c.customerNumber || null;
  if (c.customerType !== undefined) row.customer_type = c.customerType;
  if (c.salutation !== undefined) row.salutation = c.salutation || null;
  if (c.title !== undefined) row.title = c.title || null;
  if (c.firstName !== undefined) row.first_name = c.firstName || null;
  if (c.lastName !== undefined) row.last_name = c.lastName || null;
  if (c.name !== undefined) row.name = c.name;
  if (c.name2 !== undefined) row.name_2 = c.name2 || null;
  if (c.name3 !== undefined) row.name_3 = c.name3 || null;
  if (c.address !== undefined) row.address = c.address;
  if (c.street !== undefined) row.street = c.street;
  if (c.zip !== undefined) row.zip = c.zip;
  if (c.city !== undefined) row.city = c.city;
  if (c.email !== undefined) row.email = c.email;
  if (c.phone !== undefined) row.phone = c.phone;
  if (c.website !== undefined) row.website = c.website;
  if (c.contacts !== undefined) row.contacts = c.contacts;
  if (c.frameworkContracts !== undefined) row.framework_contracts = c.frameworkContracts;
  // 0 wie NULL behandeln — beides heisst "keine abweichende Vereinbarung",
  // und NULL ist im Schema die einzige Schreibweise dafuer. Das Feld ganz
  // auszulassen hiesse dagegen "nicht anfassen".
  if (c.paymentTermsDays !== undefined) {
    row.payment_terms_days =
      typeof c.paymentTermsDays === 'number' && c.paymentTermsDays > 0
        ? c.paymentTermsDays
        : null;
  }
  if (c.defaultDiscountPercent !== undefined) row.default_discount_percent = c.defaultDiscountPercent;
  // 0/negativ heisst geloescht → NULL, nicht die Zahl 0: NULL ist im Schema
  // die einzige Schreibweise fuer "kein Skonto".
  if (c.skonto !== undefined) {
    row.skonto_percent = c.skonto && c.skonto.percent > 0 ? c.skonto.percent : null;
    row.skonto_days = c.skonto && c.skonto.days > 0 ? c.skonto.days : null;
  }
  if (c.ratingZahlungsmoral !== undefined) row.rating_zahlungsmoral = c.ratingZahlungsmoral;
  if (c.ratingZusammenarbeit !== undefined) row.rating_zusammenarbeit = c.ratingZusammenarbeit;
  if (c.ratingWirtschaftlichkeit !== undefined) row.rating_wirtschaftlichkeit = c.ratingWirtschaftlichkeit;
  if (c.ratingOverall !== undefined) row.rating_overall = c.ratingOverall;
  if (c.ratingNotes !== undefined) row.rating_notes = c.ratingNotes;
  if (c.notes !== undefined) row.notes = c.notes;
  if (c.branche !== undefined) row.branche = c.branche || null;
  // Koordinaten: '' gibt es nicht, aber ein bewusst geleertes Feld muss null
  // werden — sonst schriebe die Zahl 0 den Atlantik in die Karte.
  if (c.lat !== undefined) row.lat = Number.isFinite(c.lat) ? c.lat : null;
  if (c.lng !== undefined) row.lng = Number.isFinite(c.lng) ? c.lng : null;
  if (c.geoQuality !== undefined) row.geo_quality = c.geoQuality || null;
  if (c.geoManual !== undefined) row.geo_manual = !!c.geoManual;
  if (c.changeLog !== undefined) row.change_log = c.changeLog;
  return row;
};

export const toQuoteRow = (q: Partial<Quote>): Record<string, any> => {
  const row: Record<string, any> = {};
  if (q.quoteNumber !== undefined) row.quote_number = q.quoteNumber;
  if (q.orderNumber !== undefined) row.order_number = q.orderNumber;
  if (q.customerId !== undefined) row.customer_id = q.customerId;
  if (q.projectId !== undefined) row.project_id = q.projectId;
  if (q.status !== undefined) row.status = q.status;
  if (q.title !== undefined) row.title = q.title;
  if (q.quoteDate !== undefined) row.quote_date = q.quoteDate;
  if (q.validUntil !== undefined) row.valid_until = q.validUntil;
  if (q.items !== undefined) row.items = q.items;
  if (q.discountPercent !== undefined) row.discount_percent = q.discountPercent;
  if (q.taxRate !== undefined) row.tax_rate = q.taxRate;
  if (q.netTotal !== undefined) row.net_total = q.netTotal;
  if (q.grossTotal !== undefined) row.gross_total = q.grossTotal;
  if (q.details !== undefined) row.details = q.details;
  if (q.introText !== undefined) row.intro_text = q.introText;
  if (q.outroText !== undefined) row.outro_text = q.outroText;
  if (q.paymentTerms !== undefined) row.payment_terms = q.paymentTerms;
  if (q.pdfUrl !== undefined) row.pdf_url = q.pdfUrl;
  if (q.sentAt !== undefined) row.sent_at = q.sentAt;
  if (q.sentTo !== undefined) row.sent_to = q.sentTo;
  if (q.customerSnapshot !== undefined) row.customer_snapshot = q.customerSnapshot;
  if (q.acceptedAt !== undefined) row.accepted_at = q.acceptedAt;
  if (q.rejectedAt !== undefined) row.rejected_at = q.rejectedAt;
  if (q.followUpDate !== undefined) row.follow_up_date = q.followUpDate;
  if (q.followUpDoneAt !== undefined) row.follow_up_done_at = q.followUpDoneAt;
  if (q.followUpNotifiedAt !== undefined) row.follow_up_notified_at = q.followUpNotifiedAt;
  return row;
};

// ─── Customers ───────────────────────────────────────────────────────────────

// Seitenweise: customers hat 1244 Zeilen (PROD 23.10.2026), PostgREST kappt
// still bei 1000 — die alphabetisch letzten Kunden fehlten in Liste, Auswahl
// und Dublettenpruefung. `id` als zweiter Schluessel macht das Blaettern
// eindeutig (gleichnamige Kunden).
export const fetchCustomers = async (
  opts: { includeArchived?: boolean } = {},
): Promise<Customer[]> => {
  const rows = await ladeAlleSeiten<Record<string, unknown>>((von, bis) => {
    let q = supabase.from('customers').select('*');
    if (!opts.includeArchived) q = q.is('archived_at', null);
    return q.order('name').order('id').range(von, bis);
  });
  return rows.map(mapCustomerRow);
};

export const fetchCustomer = async (id: string): Promise<Customer | null> => {
  const { data, error } = await supabase.from('customers').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data ? mapCustomerRow(data) : null;
};

/**
 * Naechste freie Kundennummer im Buchstaben-Bereich des Namens (RPC,
 * race-sicher). Wirft mit klarer Meldung, wenn der Name keinen Buchstaben
 * enthaelt oder der Bereich voll ist — dann manuell vergeben.
 */
export const nextCustomerNumberForName = async (name: string): Promise<string> => {
  const range = rangeForName(name);
  if (!range) {
    throw new Error(
      `Für „${name}“ lässt sich kein Nummernkreis bestimmen — bitte Kundennummer manuell vergeben.`,
    );
  }
  const { data, error } = await supabase.rpc('next_customer_number', {
    p_start: range.start,
    p_end: range.end,
  });
  if (error) throw new Error(describeError(error));
  if (!data) {
    throw new Error(
      `Nummernkreis ${range.key} (${range.start}–${range.end}) ist voll — bitte Kundennummer manuell vergeben.`,
    );
  }
  return String(data);
};

export const createCustomer = async (
  c: Omit<Customer, 'id' | 'createdBy' | 'createdAt' | 'updatedAt'>
): Promise<Customer> => {
  // Ohne Nummer: bereichsweise Vergabe nach Anfangsbuchstabe. Mit Nummer
  // (Listen-Import, manuelle Eingabe): Nummer bleibt, unique-Index sichert ab.
  const autoAssign = !c.customerNumber;
  // RPC-Pick und INSERT sind nicht atomar — der Advisory-Lock deckt nur das
  // Aussuchen ab, harte Garantie ist der Unique-Index. Faellt uns dazwischen
  // ein paralleler Anleger in die Nummer (23505), holen wir eine frische und
  // versuchen es erneut. Vom Aufrufer gesetzte Nummern bleiben unangetastet:
  // dort ist die Kollision ein echter Konflikt und muss sichtbar werden.
  const MAX_ATTEMPTS = 3;
  for (let attempt = 1; ; attempt++) {
    const withNumber = autoAssign
      ? { ...c, customerNumber: await nextCustomerNumberForName(c.name) }
      : c;
    const { data, error } = await supabase
      .from('customers')
      .insert([toCustomerRow(withNumber)])
      .select('*')
      .single();
    if (!error) return mapCustomerRow(data);
    if (!autoAssign || error.code !== '23505' || attempt >= MAX_ATTEMPTS) throw error;
  }
};

export const updateCustomer = async (id: string, updates: Partial<Customer>): Promise<void> => {
  const row = toCustomerRow(updates);
  row.updated_at = new Date().toISOString();
  const { error } = await supabase.from('customers').update(row).eq('id', id);
  if (error) throw error;
};

/**
 * Kundenstamm speichern, wenn die Rahmenvertraege geaendert wurden.
 *
 * framework_contracts ist EIN jsonb und geht als ganze Liste raus — ein
 * unverankertes Schreiben aus einer aelteren Akte loeschte einen Vertrag,
 * den inzwischen eine Angebotsannahme angehaengt hat. Deshalb: frisch lesen,
 * Dreiweg-Abgleich (mergeFrameworkContractsOnSave), verankert auf updated_at
 * schreiben; bei Konflikt neu lesen, hoechstens drei Versuche. Ein Lesefehler
 * bricht ab, statt ohne Abgleich zu schreiben.
 *
 * Gibt die tatsaechlich gespeicherte Vertragsliste zurueck.
 */
export const updateCustomerMergingFrameworkContracts = async (
  id: string,
  updates: Partial<Customer>,
  baseContracts: FrameworkContract[],
): Promise<FrameworkContract[]> => {
  const draftContracts = updates.frameworkContracts ?? [];
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const { data: row, error: readErr } = await supabase
      .from('customers').select('*').eq('id', id).maybeSingle();
    if (readErr) throw readErr;
    if (!row) throw new Error('Kunde nicht gefunden — nicht gespeichert.');
    const merged = mergeFrameworkContractsOnSave(
      baseContracts,
      draftContracts,
      mapCustomerRow(row).frameworkContracts ?? [],
    );
    const payload = toCustomerRow({ ...updates, frameworkContracts: merged });
    payload.updated_at = new Date().toISOString();
    let write = supabase.from('customers').update(payload).eq('id', id);
    // Altbestand ohne updated_at: NULL trifft kein eq, nur is.
    write = row.updated_at == null ? write.is('updated_at', null) : write.eq('updated_at', row.updated_at);
    const { data, error } = await write.select('id');
    if (error) throw error;
    if (data?.length) return merged;
  }
  throw new Error('Rahmenverträge konnten nicht gespeichert werden (Kunde wird parallel geändert) — bitte erneut versuchen.');
};

/**
 * Koordinaten aus dem Batch-Geocoding schreiben (services/geocodeBackfill.ts).
 *
 * Bewusst eine eigene Funktion statt updateCustomer:
 *  - `.eq('geo_manual', false)` ist der zweite Riegel gegen das Ueberschreiben
 *    von Hand gesetzter Koordinaten — der Job filtert sie schon vorher heraus,
 *    aber diese Zusage darf nicht allein an der Auswahl haengen.
 *  - `updated_at` bleibt unberuehrt: eine Verortung ist keine Stammdaten-
 *    aenderung des Kunden und soll ihn nicht in den "zuletzt geaendert"-Listen
 *    nach oben spuelen.
 *
 * Rueckgabe: wurde tatsaechlich geschrieben? false = manuell verortet.
 */
export const updateCustomerGeo = async (
  id: string,
  geo: { lat: number; lng: number; quality?: string },
): Promise<boolean> => {
  const { data, error } = await supabase
    .from('customers')
    .update({ lat: geo.lat, lng: geo.lng, geo_quality: geo.quality ?? null })
    .eq('id', id)
    .eq('geo_manual', false)
    .select('id');
  if (error) throw error;
  return (data?.length ?? 0) > 0;
};

/**
 * Archivieren ersetzt Loeschen: Rechnungen referenzieren den Kunden (FK +
 * GoBD-Aufbewahrung) — ein Delete scheiterte frueher mit einem kryptischen
 * FK-Fehler. Die Kundennummer bleibt dauerhaft vergeben.
 */
export const archiveCustomer = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('customers')
    .update({ archived_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
};

/**
 * Wiederherstellen ist das Gegenstueck zum Archivieren — NICHT zum Merge.
 *
 * Ein zusammengefuehrter Kunde hat keine Projekte, Angebote, Preise und
 * Guthaben mehr: die liegen beim Ziel. Zurueck ins Aktive geholt waere er eine
 * leere Dublette, die in jeder Auswahlliste neben dem echten Kunden steht —
 * genau die Verwechslung, wegen der zusammengefuehrt wurde. Die UI verbirgt
 * den Knopf bereits; hier steht der Riegel, an dem auch ein direkter
 * Service-Aufruf scheitert.
 */
export const restoreCustomer = async (id: string): Promise<void> => {
  const { data, error: readErr } = await supabase
    .from('customers')
    .select('merged_into_id')
    .eq('id', id)
    .maybeSingle();
  if (readErr) throw readErr;
  if (data?.merged_into_id) {
    throw new Error(
      'Dieser Kunde wurde in einen anderen Kunden überführt und kann nicht wiederhergestellt werden. ' +
        'Seine Projekte, Angebote und Konditionen liegen beim Zielkunden — bitte dort weiterarbeiten oder einen neuen Kunden anlegen.',
    );
  }
  const { error } = await supabase
    .from('customers')
    .update({ archived_at: null, archived_by: null, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
};

/**
 * Der Referenz-Merge war erfolgreich, nur die Stammdaten-Uebernahme danach
 * nicht. Eigene Klasse, damit der Aufrufer das NICHT als gescheiterten Merge
 * anzeigt: die Projekte, Angebote, Preise und Guthaben sind bereits umgehaengt,
 * ein zweiter Versuch waere ins Leere gelaufen.
 */
export class MergeCarryoverError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MergeCarryoverError';
  }
}

/** ''/undefined/null sind hier dasselbe: nichts, das erhaltenswert waere. */
const isBlank = (v: unknown): boolean => typeof v !== 'string' || !v.trim();
/** Vergleichsschluessel fuer Dublettenpruefung — getrimmt, case-insensitiv. */
const matchKey = (v?: string | null): string => (v ?? '').trim().toLowerCase();

/** Skalare, die aus der Quelle nachgezogen werden duerfen — nur in Luecken. */
const CARRYOVER_SCALARS = ['email', 'phone', 'website', 'salutation', 'title', 'firstName', 'lastName', 'notes'] as const;

/**
 * Was beim Zusammenfuehren vom Quell- auf den Zielkunden nachgezogen wird.
 *
 * Der RPC haengt nur Referenzen um (Projekte, Angebote, Preise, Guthaben) — die
 * Stammdaten der Quelle fielen bisher ersatzlos weg: die Telefonnummer, unter
 * der man die Firma jahrelang erreicht hat, und saemtliche Ansprechpartner
 * waren nach dem Merge nur noch im Archiv zu finden.
 *
 * Leitregel: der ZIELKUNDE gewinnt immer. Gefuellt wird ausschliesslich, was
 * beim Ziel leer ist; Notizen werden NICHT zusammengeklebt (aus zwei Texten
 * wird sonst ein dritter, den niemand geschrieben hat). Listen wachsen dagegen:
 * ein Ansprechpartner, den das Ziel nicht kennt, ist echter Zugewinn.
 *
 * Pur — testbar ohne Supabase (Projektkonvention).
 */
export const computeMergeCarryover = (source: Customer, target: Customer): Partial<Customer> => {
  const out: Partial<Customer> = {};

  for (const key of CARRYOVER_SCALARS) {
    if (isBlank(target[key]) && !isBlank(source[key])) {
      (out as Record<string, unknown>)[key] = (source[key] as string).trim();
    }
  }
  // customerType kennt kein "leer": fehlend heisst in der App 'company'. Nur
  // ein wirklich ungesetztes Zielfeld wird darum aus der Quelle belegt.
  if (target.customerType === undefined && source.customerType !== undefined) {
    out.customerType = source.customerType;
  }

  // ── Ansprechpartner: anhaengen, was das Ziel nicht schon hat ──────────────
  // Dublette = gleicher Name (case-insensitiv) ODER gleiche E-Mail. Die
  // Vergleichsmengen wachsen mit, sodass auch Dubletten INNERHALB der Quelle
  // nur einmal ankommen.
  const targetContacts = target.contacts ?? [];
  const usedContactIds = new Set(targetContacts.map((c) => c.id).filter(Boolean));
  const contactNames = new Set(targetContacts.map((c) => matchKey(c.name)).filter(Boolean));
  const contactEmails = new Set(targetContacts.map((c) => matchKey(c.email)).filter(Boolean));
  const addedContacts: CustomerContact[] = [];
  for (const c of source.contacts ?? []) {
    const name = matchKey(c.name);
    const email = matchKey(c.email);
    if ((name && contactNames.has(name)) || (email && contactEmails.has(email))) continue;
    if (name) contactNames.add(name);
    if (email) contactEmails.add(email);
    // Id nur bei Kollision neu vergeben — sonst bleibt sie, damit Verweise
    // aus der Quelle (Angebote, Historie) weiterhin passen.
    const id = !c.id || usedContactIds.has(c.id) ? crypto.randomUUID() : c.id;
    usedContactIds.add(id);
    addedContacts.push({ ...c, id });
  }
  if (addedContacts.length) out.contacts = [...targetContacts, ...addedContacts];

  // ── Rahmenvertraege: analog ───────────────────────────────────────────────
  // Dublette = gleiche Id, gleiche Vertragsnummer, oder (ohne Nummer) gleicher
  // Titel. Der Standard-Rahmenvertrag bleibt EINER je Kunde: hat das Ziel
  // schon einen, kommt der aus der Quelle ohne Standard-Kennzeichen an.
  const targetContracts = target.frameworkContracts ?? [];
  const usedContractIds = new Set(targetContracts.map((f) => f.id).filter(Boolean));
  const contractNumbers = new Set(targetContracts.map((f) => matchKey(f.number)).filter(Boolean));
  const contractTitles = new Set(targetContracts.map((f) => matchKey(f.title)).filter(Boolean));
  let defaultTaken = targetContracts.some((f) => f.isDefault);
  const addedContracts: FrameworkContract[] = [];
  for (const f of source.frameworkContracts ?? []) {
    const number = matchKey(f.number);
    const title = matchKey(f.title);
    if (f.id && usedContractIds.has(f.id)) continue;
    if (number && contractNumbers.has(number)) continue;
    if (!number && title && contractTitles.has(title)) continue;
    if (number) contractNumbers.add(number);
    if (title) contractTitles.add(title);
    const id = !f.id || usedContractIds.has(f.id) ? crypto.randomUUID() : f.id;
    usedContractIds.add(id);
    const carried: FrameworkContract = { ...f, id };
    if (carried.isDefault) {
      if (defaultTaken) delete carried.isDefault;
      else defaultTaken = true;
    }
    addedContracts.push(carried);
  }
  if (addedContracts.length) out.frameworkContracts = [...targetContracts, ...addedContracts];

  return out;
};

/**
 * Der Historien-Eintrag zur Uebernahme. diffCustomerChanges deckt die
 * historisierten Stammdatenfelder ab; Notizen und die beiden Listen stehen dort
 * bewusst nicht drin, tauchen hier aber als eigene Zeilen auf — sonst waere im
 * Log nicht nachvollziehbar, woher zwei neue Ansprechpartner kommen.
 * Pur, damit der Wortlaut testbar bleibt.
 */
export const describeMergeCarryover = (
  source: Customer,
  target: Customer,
  carryover: Partial<Customer>,
): CustomerChange[] => {
  if (!Object.keys(carryover).length) return [];
  const label = source.customerNumber ? `${source.name} (${source.customerNumber})` : source.name;
  const changes: CustomerChange[] = [
    { field: 'Zusammenführung', from: undefined, to: `Daten übernommen aus ${label}` },
    ...diffCustomerChanges(target, { ...target, ...carryover }),
  ];
  if (carryover.notes !== undefined) {
    changes.push({ field: 'Notizen', from: undefined, to: 'aus zusammengeführtem Kunden übernommen' });
  }
  if (carryover.contacts) {
    changes.push({
      field: 'Ansprechpartner',
      from: String((target.contacts ?? []).length),
      to: String(carryover.contacts.length),
    });
  }
  if (carryover.frameworkContracts) {
    changes.push({
      field: 'Rahmenverträge',
      from: String((target.frameworkContracts ?? []).length),
      to: String(carryover.frameworkContracts.length),
    });
  }
  return changes;
};

/**
 * Fuehrt source in target zusammen (RPC, transaktional). Projekte, Angebote,
 * Kundenpreise und Guthaben wandern zum Ziel; RECHNUNGEN UND KUNDENNUMMER
 * BLEIBEN BEIM ARCHIVIERTEN QUELLKUNDEN — Nummern werden nie zusammengelegt.
 *
 * Danach — bewusst ausserhalb der RPC-Transaktion — der Stammdaten-Nachzug
 * (computeMergeCarryover). Scheitert er, ist der Merge selbst trotzdem gueltig:
 * das meldet MergeCarryoverError, damit der Aufrufer nicht zum Wiederholen
 * auffordert.
 */
export const mergeCustomers = async (sourceId: string, targetId: string): Promise<void> => {
  const { error } = await supabase.rpc('merge_customers', {
    p_source: sourceId,
    p_target: targetId,
  });
  if (error) throw new Error(describeError(error));

  try {
    const [source, target] = await Promise.all([fetchCustomer(sourceId), fetchCustomer(targetId)]);
    if (!source || !target) return;
    const carryover = computeMergeCarryover(source, target);
    if (!Object.keys(carryover).length) return;

    const changes = describeMergeCarryover(source, target, carryover);
    const updates: Partial<Customer> = { ...carryover };
    if (changes.length) {
      // Fehlt die Anmeldung, bleibt der Eintrag ohne Bearbeiter — er ist
      // wichtiger als sein Absender.
      const auth = await supabase.auth.getUser().then(
        (r) => r.data.user?.email ?? undefined,
        () => undefined,
      );
      updates.changeLog = appendChangeEntry(
        target.changeLog ?? [],
        changes,
        new Date().toISOString(),
        auth,
      );
    }
    await updateCustomer(targetId, updates);
  } catch (e) {
    throw new MergeCarryoverError(
      'Die Zusammenführung selbst war erfolgreich — Projekte, Angebote, Kundenpreise und Guthaben ' +
        'liegen bereits beim Zielkunden. Nur die Übernahme der Stammdaten (Ansprechpartner, Kontaktdaten) ' +
        `ist fehlgeschlagen: ${describeError(e)}. Bitte diese Felder am Zielkunden von Hand prüfen. ` +
        'Ein erneutes Zusammenführen ist NICHT nötig.',
    );
  }
};

/** Status, die zaehlen: alles ab Ausstellung ausser Storno-Leichen. */
const REVENUE_EXCLUDED = new Set(['DRAFT', 'READY_FOR_REVIEW', 'APPROVED', 'ISSUING', 'CANCELLED']);

/**
 * Kennzahlen der Kundenuebersicht in ZWEI Abfragen statt N+1: Umsatz aus
 * invoices, Projektanzahl aus projects, jeweils clientseitig gruppiert.
 * Stornobelege tragen negative Summen und mindern damit automatisch; der
 * Umsatz gemergter Quellkunden wird dem Ziel zugerechnet (foldMergedRevenue).
 *
 * Alle drei seitenweise: customers hat 1244 Zeilen (PROD 23.10.2026), ohne
 * `.range()` fiel ein Teil der Merge-Zuordnung still weg; invoices und
 * projects wachsen auf dieselbe 1000er-Grenze zu.
 */
export const fetchCustomerStats = async (): Promise<Map<string, CustomerStats>> => {
  const [invRows, projRows, custRows] = await Promise.all([
    // `id` mitselektiert: ladeAlleSeiten verwirft daran Dubletten, die ein
    // zwischen zwei Seiten angelegter Beleg erzeugt (sonst doppelter Umsatz).
    ladeAlleSeiten<{ id: string; customer_id: string; status: string; gross_total_cents: number | null }>((von, bis) =>
      supabase.from('invoices').select('id, customer_id, status, gross_total_cents').order('id').range(von, bis)),
    ladeAlleSeiten<{ customer_id: string }>((von, bis) =>
      supabase.from('projects').select('id, customer_id').not('customer_id', 'is', null).order('id').range(von, bis)),
    ladeAlleSeiten<{ id: string; merged_into_id: string | null }>((von, bis) =>
      supabase.from('customers').select('id, merged_into_id').order('id').range(von, bis)),
  ]);

  const revenue = new Map<string, number>();
  for (const r of invRows) {
    if (REVENUE_EXCLUDED.has(r.status)) continue;
    revenue.set(r.customer_id, (revenue.get(r.customer_id) ?? 0) + (r.gross_total_cents ?? 0));
  }
  const folded = foldMergedRevenue(
    revenue,
    custRows.map((c) => ({ id: c.id, mergedIntoId: c.merged_into_id ?? undefined })),
  );

  const stats = new Map<string, CustomerStats>();
  const ensure = (id: string) => {
    const s = stats.get(id) ?? { revenueCents: 0, projectCount: 0 };
    stats.set(id, s);
    return s;
  };
  for (const [id, cents] of folded) ensure(id).revenueCents = cents;
  for (const p of projRows) ensure(p.customer_id).projectCount += 1;
  return stats;
};

// ─── Customer prices ─────────────────────────────────────────────────────────

export const fetchCustomerPrices = async (customerId: string): Promise<CustomerPrice[]> => {
  const { data, error } = await supabase
    .from('customer_prices')
    .select('*')
    .eq('customer_id', customerId);
  if (error) throw error;
  return (data || []).map(mapCustomerPriceRow);
};

export const upsertCustomerPrice = async (
  p: Omit<CustomerPrice, 'id'> & { id?: string }
): Promise<void> => {
  const row: Record<string, any> = {
    customer_id: p.customerId,
    catalog_item_id: p.catalogItemId,
    unit_price: p.unitPrice ?? null,
    discount_percent: p.discountPercent ?? null,
    note: p.note ?? null,
  };
  if (p.id) row.id = p.id;
  const { error } = await supabase
    .from('customer_prices')
    .upsert(row, { onConflict: 'customer_id,catalog_item_id' });
  if (error) throw error;
};

export const deleteCustomerPrice = async (id: string): Promise<void> => {
  const { error } = await supabase.from('customer_prices').delete().eq('id', id);
  if (error) throw error;
};

// ─── Quotes ──────────────────────────────────────────────────────────────────

// Seitenweise: quotes waechst ungefiltert auf die stille 1000er-Grenze von
// PostgREST zu (Vorfall 23.10.2026, Kundenliste 1000 von 1244).
export const fetchQuotes = async (): Promise<Quote[]> => {
  const rows = await ladeAlleSeiten<Record<string, unknown>>((von, bis) =>
    supabase
      .from('quotes')
      .select('*, customers(name)')
      .order('created_at', { ascending: false })
      .order('id')
      .range(von, bis));
  return rows.map(mapQuoteRow);
};

export const fetchQuotesForCustomer = async (customerId: string): Promise<Quote[]> => {
  const { data, error } = await supabase
    .from('quotes')
    .select('*, customers(name)')
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(mapQuoteRow);
};

export const createQuote = async (input: {
  customerId: string;
  title?: string;
  items?: QuoteItem[];
  discountPercent?: number;
  taxRate?: number;
  validUntil?: string;
  introText?: string;
  outroText?: string;
  paymentTerms?: string;
  details?: QuoteDetails;
  /**
   * Endsummen (ohne NEP, calculateQuoteTotals). Fehlen sie, werden sie aus
   * `items` gerechnet, sobald Positionen UND Steuersatz mitkommen — ein
   * Angebot mit Positionen soll nie mit 0 € in der Liste stehen.
   */
  netTotal?: number;
  grossTotal?: number;
  /**
   * Nur fuer eingelesene Alt-/Fremdbelege (PDF-Import): Status und Belegdaten
   * wie im Original. Fehlen sie, gelten die Spaltenvorgaben (draft, heute).
   */
  status?: Quote['status'];
  quoteDate?: string;
  sentAt?: string;
  acceptedAt?: string;
  rejectedAt?: string;
  /**
   * Typziffer des Nummernkreises (1 = SiGeKo, 2 = SiFa, 3 = Sonstige). Sie
   * wird beim ANLEGEN gewaehlt, weil die Angebotsnummer hier gezogen wird und
   * spaeter nicht mehr wechseln darf.
   */
  typeDigit?: TypeDigit;
}): Promise<Quote> => {
  const quoteNumber = await nextDocumentNumber('AN', input.typeDigit ?? '1');

  const row: Record<string, any> = {
    quote_number: quoteNumber,
    customer_id: input.customerId,
  };
  if (input.title !== undefined) row.title = input.title;
  if (input.items !== undefined) row.items = input.items;
  if (input.discountPercent !== undefined) row.discount_percent = input.discountPercent;
  if (input.taxRate !== undefined) row.tax_rate = input.taxRate;
  if (input.validUntil !== undefined) row.valid_until = input.validUntil;
  if (input.introText !== undefined) row.intro_text = input.introText;
  if (input.outroText !== undefined) row.outro_text = input.outroText;
  if (input.paymentTerms !== undefined) row.payment_terms = input.paymentTerms;
  if (input.details !== undefined) row.details = input.details;
  if (input.status !== undefined) row.status = input.status;
  if (input.quoteDate !== undefined) row.quote_date = input.quoteDate;
  if (input.sentAt !== undefined) row.sent_at = input.sentAt;
  if (input.acceptedAt !== undefined) row.accepted_at = input.acceptedAt;
  if (input.rejectedAt !== undefined) row.rejected_at = input.rejectedAt;
  if (input.netTotal !== undefined) row.net_total = input.netTotal;
  if (input.grossTotal !== undefined) row.gross_total = input.grossTotal;
  if (
    input.items !== undefined && input.taxRate !== undefined
    && input.netTotal === undefined && input.grossTotal === undefined
  ) {
    const totals = calculateQuoteTotals(input.items, input.discountPercent ?? 0, input.taxRate);
    row.net_total = totals.netTotal;
    row.gross_total = totals.grossTotal;
  }

  const { data, error } = await supabase
    .from('quotes')
    .insert([row])
    .select('*, customers(name)')
    .single();
  if (error) throw error;
  return mapQuoteRow(data);
};

/**
 * The anchored write missed and a second, merged write missed as well: somebody
 * keeps saving the same quote. `server` is the row as last read — the editor
 * should take it over (or show it) instead of retrying blindly.
 */
export class QuoteConflictError extends Error {
  constructor(public server: Quote) {
    super('Das Angebot wurde parallel gespeichert und konnte nicht zusammengeführt werden.');
    this.name = 'QuoteConflictError';
  }
}

/** The update hit no row: the quote is deleted or RLS refuses it. Nothing was written. */
export class QuoteNotSavedError extends Error {
  constructor(message = 'Das Angebot wurde nicht gespeichert (gelöscht oder keine Berechtigung).') {
    super(message);
    this.name = 'QuoteNotSavedError';
  }
}

export interface UpdateQuoteOptions {
  /**
   * `quotes.updated_at` exactly as the server returned it with the state the
   * edit is based on. Set → the write only lands on that state; a miss is
   * merged (see quoteMerge.ts). Unset → unconditional write as before.
   */
  expectedUpdatedAt?: string | null;
  /** The quote the edit started from (needed for the three-way merge). */
  base?: Quote | null;
}

export interface UpdateQuoteResult {
  /** `updated_at` of the row after this write — the next anchor. */
  updatedAt: string;
  /** Full server row after a resolved conflict (merged state); null if the anchor held. */
  merged: Quote | null;
}

const QUOTE_SAVE_COLUMNS = 'id, updated_at';

export const updateQuote = async (
  id: string,
  updates: Partial<Quote>,
  opts: UpdateQuoteOptions = {},
): Promise<UpdateQuoteResult> => {
  const row = toQuoteRow(updates);
  row.updated_at = new Date().toISOString();
  const anchor = opts.expectedUpdatedAt || null;

  if (!anchor) {
    // details wird als GANZES jsonb geschrieben. Der Nachfass-Verlauf haengt aber
    // im selben Objekt und entsteht ausserhalb des Editors: haette ein Anderer
    // waehrenddessen nachgefasst, naehme der Autosave den Eintrag wieder zurueck.
    // Deshalb wird der Verlauf vor dem Schreiben vom Server geholt und
    // zusammengefuehrt — der Editor kann ihn ohnehin nicht aendern.
    // Ein Lesefehler bricht ab: ohne Serverstand wuerde der Verlauf still
    // ueberschrieben. (Mit Anker erledigt das die Zusammenfuehrung unten.)
    if (row.details !== undefined) {
      const { data: current, error: readErr } = await supabase
        .from('quotes').select('details').eq('id', id).maybeSingle();
      if (readErr) throw readErr;
      if (!current) throw new QuoteNotSavedError();
      const serverLog = ((current.details as QuoteDetails | null)?.followUpLog) || [];
      if (serverLog.length > 0) {
        const local = ((row.details as QuoteDetails | null)?.followUpLog) || [];
        const seen = new Set(serverLog.map((e) => `${e.at}|${e.by ?? ''}`));
        row.details = {
          ...(row.details as QuoteDetails),
          followUpLog: [...serverLog, ...local.filter((e) => !seen.has(`${e.at}|${e.by ?? ''}`))],
        };
      }
    }
    const { data, error } = await supabase
      .from('quotes').update(row).eq('id', id).select(QUOTE_SAVE_COLUMNS);
    if (error) throw error;
    if (!data?.length) throw new QuoteNotSavedError();
    return { updatedAt: String(data[0].updated_at), merged: null };
  }

  // DER ANKER — gleiches Muster wie saveProjectToCloud: nur auf den Stand
  // schreiben, auf dem die Aenderung beruht. Verglichen wird der Wert, den
  // Postgres selbst geliefert hat (zeichengleich), nie ein eigener Zeitstempel.
  const first = await supabase
    .from('quotes').update(row).eq('id', id).eq('updated_at', anchor).select(QUOTE_SAVE_COLUMNS);
  if (first.error) throw first.error;
  if (first.data?.length) return { updatedAt: String(first.data[0].updated_at), merged: null };

  // Keine Zeile getroffen: geloescht/RLS — oder jemand anderes hat gespeichert.
  const { data: currentRow, error: readErr } = await supabase
    .from('quotes').select('*, customers(name)').eq('id', id).maybeSingle();
  if (readErr) throw readErr;
  if (!currentRow) throw new QuoteNotSavedError();
  const server = mapQuoteRow(currentRow);
  // Ohne Ausgangsstand ist keine Zusammenfuehrung moeglich (geloescht vs. nie
  // gesehen ist nicht unterscheidbar) — dann lieber melden als ueberschreiben.
  if (!opts.base) throw new QuoteConflictError(server);
  // Der Ausgangsstand war ein Entwurf, inzwischen hat ein anderer Tab das
  // Angebot versendet/angenommen/abgelehnt: Inhalte (Positionen, Preise,
  // Texte) duerfen dann NICHT mehr hineinzusammengefuehrt werden — das
  // Angebot ist ein Beleg. Nur das Nachfassdatum bleibt aenderbar. Der Editor
  // zeigt den Konflikt; die lokale Sicherung landet im Modus "blockiert".
  if (opts.base.status === 'draft' && server.status !== 'draft'
    && Object.keys(updates).some((k) => k !== 'followUpDate')) {
    throw new QuoteConflictError(server);
  }

  const patch = mergeQuoteUpdate(opts.base, updates, server);
  const mergedRow = toQuoteRow(patch);
  mergedRow.updated_at = new Date().toISOString();
  const second = await supabase
    .from('quotes').update(mergedRow).eq('id', id).eq('updated_at', currentRow.updated_at)
    .select('*, customers(name)');
  if (second.error) throw second.error;
  if (!second.data?.length) throw new QuoteConflictError(server);
  console.warn(`[crmService] Angebot ${id} wurde zwischenzeitlich gespeichert; Aenderungen zusammengefuehrt.`);
  const merged = mapQuoteRow(second.data[0]);
  return { updatedAt: merged.updatedAt, merged };
};

export const deleteQuote = async (id: string): Promise<void> => {
  const { error } = await supabase.from('quotes').delete().eq('id', id);
  if (error) throw error;
};

/**
 * Dupliziert ein Angebot als neuen Entwurf fuer (optional) einen anderen Kunden.
 * Zieht eine frische Angebotsnummer (AN) und kopiert Titel, Positionen (mit
 * NEUEN Positions-IDs, aber unveraenderten sectionId/nep/bemerkung/posNumber/
 * excluded), Rabatt/Steuer, Texte und details (inkl. Standortadresse — der Nutzer
 * passt sie anschliessend an). Endsummen werden ueber calculateQuoteTotals neu
 * berechnet (ohne NEP und ohne gestrichene Positionen). Status = draft.
 */
export const duplicateQuote = async (
  sourceQuote: Quote,
  targetCustomerId: string
): Promise<Quote> => {
  // Typkreis des Ausgangsangebots beibehalten: die Kopie beschreibt dieselbe
  // Leistungsart. Altnummern ohne Typziffer landen im Regelkreis '1'.
  const quoteNumber = await nextDocumentNumber(
    'AN',
    typeDigitFromDocumentNumber(sourceQuote.quoteNumber) ?? '1',
  );

  // `excluded` wird BEWUSST mitkopiert: die Kopie beschreibt dasselbe Angebot,
  // die Streichungen gelten dort genauso weiter (im Zweifel in der Kopie mit
  // einem Klick aufhebbar). Anders als beim Speichern als VORLAGE — dort wird
  // die Streichung abgestreift, siehe QuoteEditor.handleSaveTemplate.
  const items: QuoteItem[] = (sourceQuote.items || []).map((it) => ({
    ...it,
    id: crypto.randomUUID(),
  }));
  const totals = calculateQuoteTotals(
    items,
    sourceQuote.discountPercent || 0,
    sourceQuote.taxRate || 0,
  );

  const row: Record<string, any> = {
    quote_number: quoteNumber,
    customer_id: targetCustomerId,
    status: 'draft',
    title: sourceQuote.title ?? '',
    items,
    discount_percent: sourceQuote.discountPercent || 0,
    tax_rate: sourceQuote.taxRate || 0,
    net_total: totals.netTotal,
    gross_total: totals.grossTotal,
  };
  if (sourceQuote.introText !== undefined) row.intro_text = sourceQuote.introText;
  if (sourceQuote.outroText !== undefined) row.outro_text = sourceQuote.outroText;
  if (sourceQuote.paymentTerms !== undefined) row.payment_terms = sourceQuote.paymentTerms;
  // activeTenderDocumentId zeigt auf ein Dokument des AUSGANGSangebots —
  // es wird erst nach dem Kopieren auf die neue Dokument-ID umgebogen.
  if (sourceQuote.details !== undefined) {
    const details: QuoteDetails = { ...sourceQuote.details };
    delete details.activeTenderDocumentId;
    row.details = details;
  }

  const { data, error } = await supabase
    .from('quotes')
    .insert([row])
    .select('*, customers(name)')
    .single();
  if (error) throw error;
  const created = mapQuoteRow(data);

  // Original-Ausschreibung mitnehmen: attachOriginalLv/lvOriginalMode haengen
  // an tender_documents der EIGENEN quote_id. Ohne Kopie zeigte der Schalter in
  // der Kopie "anhaengen", das PDF kaeme aber ohne Original.
  const copied = await copyTenderDocuments(sourceQuote.id, created.id);
  if (copied.ok === false) {
    console.warn(
      `[crmService] Ausschreibungs-Dokumente von ${sourceQuote.quoteNumber} konnten nicht in die Kopie `
      + `${created.quoteNumber} uebernommen werden.`,
      copied.error,
    );
  }
  const next = remapDuplicateTenderDetails(
    created.details,
    sourceQuote.details?.activeTenderDocumentId,
    copied.ok === true ? copied.idMap : null,
  );
  if (next.changed) {
    if (next.reset) {
      console.warn(`[crmService] Original-LV-Einbindung in der Kopie ${created.quoteNumber} zurueckgesetzt `
        + '(aktives Ausschreibungs-Dokument nicht kopiert).');
    }
    const { data: fixRows, error: fixErr } = await supabase
      .from('quotes').update({ details: next.details }).eq('id', created.id).select('updated_at');
    if (fixErr || !fixRows?.length) {
      console.warn('[crmService] Nachtrag der Ausschreibungs-Verknuepfung in der Kopie fehlgeschlagen:', fixErr);
    } else {
      created.details = next.details;
      // Neuer Anker: der Editor schreibt sonst gegen einen ueberholten Stand.
      created.updatedAt = String(fixRows[0].updated_at);
    }
  }
  return created;
};

/**
 * details der Angebotskopie nach dem Kopieren der Ausschreibungs-Dokumente.
 *
 * - aktives Dokument kopiert → `activeTenderDocumentId` zeigt auf die KOPIE
 *   (sonst griffe pickOriginalLv auf das neueste Dokument zurueck — womoeglich
 *   einen abgebrochenen Import);
 * - aktives Dokument nicht kopiert oder Kopie ganz gescheitert → Feld fehlt,
 *   und die Original-LV-Einbindung wird abgeschaltet, falls sie an war.
 *
 * `idMap` = alte → neue tender_documents-ID, `null` = Kopie gescheitert.
 */
export const remapDuplicateTenderDetails = (
  details: QuoteDetails | undefined,
  sourceActiveId: string | undefined,
  idMap: Record<string, string> | null,
): { details: QuoteDetails; changed: boolean; reset: boolean } => {
  const base: QuoteDetails = { ...(details || {}) };
  delete base.activeTenderDocumentId;
  const newActive = sourceActiveId && idMap ? idMap[sourceActiveId] : undefined;
  if (newActive) return { details: { ...base, activeTenderDocumentId: newActive }, changed: true, reset: false };
  const lost = idMap === null || !!sourceActiveId;
  if (lost && usesOriginalLv(base)) {
    return { details: { ...base, attachOriginalLv: false, lvOriginalMode: 'none' }, changed: true, reset: true };
  }
  return { details: base, changed: details?.activeTenderDocumentId !== undefined, reset: false };
};

const usesOriginalLv = (d: QuoteDetails | undefined): boolean =>
  !!d && (d.attachOriginalLv === true || d.lvOriginalMode === 'append' || d.lvOriginalMode === 'priced');

/**
 * Kopiert alle tender_documents eines Angebots samt Storage-Objekt auf ein
 * anderes Angebot (neuer Pfad {toQuoteId}/{kind}-{uuid}.{ext}, wie beim
 * Hochladen). Alles-oder-nichts: schlaegt ein Schritt fehl, werden die bereits
 * kopierten Objekte/Zeilen best effort wieder entfernt.
 */
const copyTenderDocuments = async (
  fromQuoteId: string,
  toQuoteId: string,
): Promise<{ ok: true; idMap: Record<string, string> } | { ok: false; error: unknown }> => {
  const createdPaths: string[] = [];
  const createdIds: string[] = [];
  const idMap: Record<string, string> = {};
  try {
    const { data: docs, error } = await supabase
      .from('tender_documents').select('*').eq('quote_id', fromQuoteId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    for (const d of docs || []) {
      const src = String(d.file_path);
      const extMatch = /\.([a-z0-9]+)$/i.exec(src);
      const ext = (extMatch?.[1] || 'bin').toLowerCase();
      const dest = `${toQuoteId}/${d.kind}-${crypto.randomUUID()}.${ext}`;
      const { error: cpErr } = await supabase.storage.from(TENDER_BUCKET).copy(src, dest);
      if (cpErr) throw cpErr;
      createdPaths.push(dest);
      const { data: ins, error: insErr } = await supabase
        .from('tender_documents')
        .insert([{
          quote_id: toQuoteId,
          kind: d.kind,
          file_path: dest,
          file_name: d.file_name ?? null,
          mime_type: d.mime_type ?? null,
        }])
        .select('id')
        .single();
      if (insErr) throw insErr;
      createdIds.push(String(ins.id));
      idMap[String(d.id)] = String(ins.id);
    }
    return { ok: true, idMap };
  } catch (err) {
    if (createdIds.length) {
      await supabase.from('tender_documents').delete().in('id', createdIds)
        .then(undefined, () => { /* best effort */ });
    }
    if (createdPaths.length) {
      await supabase.storage.from(TENDER_BUCKET).remove(createdPaths)
        .then(undefined, () => { /* best effort */ });
    }
    return { ok: false, error: err };
  }
};

// ─── Angebotsvorlagen ──────────────────────────────────────────────────────

export const fetchQuoteTemplates = async (): Promise<QuoteTemplate[]> => {
  const { data, error } = await supabase.from('quote_templates').select('*').order('name');
  if (error) throw error;
  return (data || []).map(mapQuoteTemplateRow);
};

export const createQuoteTemplate = async (
  name: string,
  payload: QuoteTemplate['payload']
): Promise<QuoteTemplate> => {
  const { data, error } = await supabase
    .from('quote_templates')
    .insert([{ name, payload }])
    .select('*')
    .single();
  if (error) throw error;
  return mapQuoteTemplateRow(data);
};

export const deleteQuoteTemplate = async (id: string): Promise<void> => {
  const { error } = await supabase.from('quote_templates').delete().eq('id', id);
  if (error) throw error;
};

// ─── Textbausteine ─────────────────────────────────────────────────────────
// Wiederverwendbare Vorlagen fuer die vier Angebots-Textfelder (siehe
// TextTemplate/TextTemplateCategory in src/types.ts, Tabelle text_templates).

const mapTextTemplateRow = (row: any): TextTemplate => ({
  id: row.id,
  category: row.category,
  name: row.name,
  content: row.content ?? '',
});

export const fetchTextTemplates = async (): Promise<TextTemplate[]> => {
  const { data, error } = await supabase
    .from('text_templates')
    .select('*')
    .order('category')
    .order('name');
  if (error) throw error;
  return (data || []).map(mapTextTemplateRow);
};

export const createTextTemplate = async (t: Omit<TextTemplate, 'id'>): Promise<TextTemplate> => {
  const { data, error } = await supabase
    .from('text_templates')
    .insert([{ category: t.category, name: t.name, content: t.content }])
    .select('*')
    .single();
  if (error) throw error;
  return mapTextTemplateRow(data);
};

export const deleteTextTemplate = async (id: string): Promise<void> => {
  const { error } = await supabase.from('text_templates').delete().eq('id', id);
  if (error) throw error;
};

export const markQuoteSent = async (
  id: string,
  args: { pdfUrl: string; sentTo: string; followUpDate: string; customerSnapshot: Customer }
): Promise<{ updatedAt: string }> => {
  const { data, error } = await supabase
    .from('quotes')
    .update({
      status: 'sent',
      sent_at: new Date().toISOString(),
      pdf_url: args.pdfUrl,
      sent_to: args.sentTo,
      follow_up_date: args.followUpDate,
      customer_snapshot: args.customerSnapshot,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select(QUOTE_SAVE_COLUMNS);
  if (error) throw error;
  // 0 Zeilen (geloescht/RLS) sah bisher aus wie Erfolg: die Mail war raus,
  // das Angebot stand weiter als Entwurf da.
  if (!data?.length) throw new QuoteNotSavedError('Versandstatus konnte nicht gespeichert werden (Angebot gelöscht oder keine Berechtigung).');
  // Neuer Anker fuer den Editor: sonst verfehlte die naechste Aktion derselben
  // Sitzung (z. B. Annahme) den Stand und endete im Konflikt.
  return { updatedAt: String(data[0].updated_at) };
};

export const rejectQuote = async (id: string): Promise<{ updatedAt: string }> => {
  const { data, error } = await supabase
    .from('quotes')
    .update({
      status: 'rejected',
      rejected_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select(QUOTE_SAVE_COLUMNS);
  if (error) throw error;
  if (!data?.length) throw new QuoteNotSavedError('Ablehnung konnte nicht gespeichert werden (Angebot gelöscht oder keine Berechtigung).');
  return { updatedAt: String(data[0].updated_at) };
};

// ─── Follow-ups ──────────────────────────────────────────────────────────────

export const fetchDueFollowUps = async (todayIso: string): Promise<Quote[]> => {
  const { data, error } = await supabase
    .from('quotes')
    .select('*, customers(name)')
    .in('status', [...FOLLOW_UP_STATUSES])
    .is('follow_up_done_at', null)
    .lte('follow_up_date', todayIso)
    .order('follow_up_date', { ascending: true });
  if (error) throw error;
  return (data || []).map(mapQuoteRow);
};

/**
 * Erledigtes Nachfassen: Verlaufseintrag schreiben und den naechsten Zyklus
 * setzen (oder das Nachfassen abschliessen). Ein Aufruf, ein Update — sonst
 * gaebe es einen Zustand mit neuem Termin, aber ohne festgehaltenen Stand.
 *
 * details wird ABSICHTLICH frisch gelesen und erst dann ergaenzt: der Editor
 * schreibt in dasselbe jsonb, und ein Nachfassen darf keine parallel
 * gespeicherte Textaenderung zuruecknehmen.
 */
export const recordFollowUp = async (
  id: string,
  input: FollowUpInput,
): Promise<{ entry: QuoteFollowUpEntry; followUpDate?: string; followUpDoneAt?: string }> => {
  const { data: current, error: readErr } = await supabase
    .from('quotes')
    .select('details, follow_up_date')
    .eq('id', id)
    .single();
  if (readErr) throw readErr;

  const record = buildFollowUpRecord(
    { details: (current?.details as QuoteDetails) ?? undefined, followUpDate: current?.follow_up_date ?? undefined },
    input,
  );

  const patch: Record<string, unknown> = {
    details: mergeFollowUpLog((current?.details as QuoteDetails) ?? undefined, record.entry),
    follow_up_date: record.followUpDate ?? null,
    follow_up_done_at: record.followUpDoneAt ?? null,
    updated_at: new Date().toISOString(),
  };
  // Neuer Zyklus: die Glocke muss erneut melden duerfen. notifyDueFollowUps
  // haelt ein Angebot sonst fuer benachrichtigt, solange der Zeitstempel nicht
  // vor dem Nachfassdatum liegt.
  if (record.continues) patch.follow_up_notified_at = null;

  const { error } = await supabase.from('quotes').update(patch).eq('id', id);
  if (error) throw error;

  return { entry: record.entry, followUpDate: record.followUpDate, followUpDoneAt: record.followUpDoneAt };
};

export const snoozeFollowUp = async (id: string, newDate: string): Promise<void> => {
  const { error } = await supabase
    .from('quotes')
    .update({
      follow_up_date: newDate,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);
  if (error) throw error;
};

/**
 * Erzeugt Glocken-Benachrichtigungen fuer faellige Nachfaesse (Admin-Aufruf beim
 * Login). Beruecksichtigt nur Angebote, die fuer den aktuellen Nachfass-Zyklus
 * noch nicht benachrichtigt wurden (follow_up_notified_at null ODER < follow_up_date).
 * Liefert die Anzahl tatsaechlich erzeugter Benachrichtigungen.
 *
 * SCHEMA-HINWEIS (project_id-Nullability): Die notifications-Tabelle ist in
 * diesem Repo nirgends per DDL definiert – sie existiert nur in der Prod-DB
 * (rls_hardening.sql referenziert sie lediglich). Daher laesst sich aus dem
 * Quellcode NICHT verifizieren, ob project_id NOT NULL ist. Das bestehende
 * Insert-Muster (createNotificationsForMentions) setzt immer eine echte project_id.
 * Deshalb ist hier jeder Insert einzeln abgesichert: schlaegt er fehl (etwa
 * wegen NOT-NULL auf project_id bei project_id=null), wird die Glocke fuer
 * dieses Angebot uebersprungen, follow_up_notified_at NICHT gesetzt (Retry beim
 * naechsten Lauf) und der Zaehler nicht erhoeht. Faellt jeder Insert aus, wird 0
 * zurueckgegeben – ohne den Login-Bootstrap zu stoeren.
 */
export const notifyDueFollowUps = async (userId: string): Promise<number> => {
  const today = new Date().toISOString().slice(0, 10);
  const due = await fetchDueFollowUps(today);
  let count = 0;

  for (const q of due) {
    // Pro Nachfass-Zyklus nur einmal benachrichtigen: bereits benachrichtigt,
    // wenn notified gesetzt ist und nicht vor dem aktuellen follow_up_date liegt.
    const notified = q.followUpNotifiedAt;
    const alreadyNotified = notified != null && (!q.followUpDate || notified >= q.followUpDate);
    if (alreadyNotified || !q.createdBy) continue;

    const sentDate = q.sentAt ? new Date(q.sentAt).toLocaleDateString('de-DE') : '—';
    const content = `Nachfassen: Angebot ${q.quoteNumber} an ${q.customerName ?? ''} (versendet am ${sentDate})`;

    const { error } = await supabase.from('notifications').insert([{
      recipient_id: q.createdBy,
      sender_id: userId,
      sender_name: 'System',
      type: 'mention',
      status: 'unread',
      content,
      project_id: null,
      project_name: q.quoteNumber,
      original_note_id: null,
    }]);
    // Insert fehlgeschlagen (z.B. NOT-NULL auf project_id): ueberspringen.
    if (error) continue;

    const { error: markErr } = await supabase
      .from('quotes')
      .update({ follow_up_notified_at: new Date().toISOString() })
      .eq('id', q.id);
    // Markierung fehlgeschlagen: nicht zaehlen, damit der naechste Lauf erneut versucht.
    if (markErr) continue;
    count++;
  }

  return count;
};

// ─── Projekt-Verknuepfung ────────────────────────────────────────────────────
// Setzt NUR projects.customer_id. Bewusst KEIN Anfassen von data: ein parallel
// offener Berichtsstand darf hier nicht ueberschrieben werden.
export const linkProjectsToCustomer = async (
  customerId: string,
  projectIds: string[]
): Promise<void> => {
  if (projectIds.length === 0) return;
  const { error } = await supabase
    .from('projects')
    .update({ customer_id: customerId })
    .in('id', projectIds);
  if (error) throw error;
};

/**
 * Aktuell verknuepfter Kunde eines Projekts (nur die Spalte, kein data-Read).
 * Gibt null zurueck, wenn keine Verknuepfung besteht.
 */
export const fetchProjectCustomerId = async (projectId: string): Promise<string | null> => {
  const { data, error } = await supabase
    .from('projects')
    .select('customer_id')
    .eq('id', projectId)
    .maybeSingle();
  if (error) throw new Error(describeError(error));
  return (data as any)?.customer_id ?? null;
};

/**
 * Einzel-Zuweisung fuer die Projekt-Stammdaten: setzt oder loescht
 * projects.customer_id. Wie linkProjectsToCustomer wird bewusst NUR diese
 * Spalte geschrieben — data bleibt unberuehrt, damit ein parallel offener
 * Berichtsstand nicht ueberschrieben wird.
 */
export const setProjectCustomer = async (
  projectId: string,
  customerId: string | null
): Promise<void> => {
  if (!projectId) throw new Error('Projekt-ID fehlt — Zuweisung nicht möglich.');
  const { error } = await supabase
    .from('projects')
    .update({ customer_id: customerId })
    .eq('id', projectId);
  if (error) throw new Error(describeError(error));
};

// Projekte/Auftraege eines Kunden fuer die Historie-Ansicht (read-only, schlank).
export const fetchProjectsForCustomer = async (
  customerId: string
): Promise<Array<{ id: string; name: string; projectNumber: string; status: string; createdAt: string }>> => {
  const { data, error } = await supabase
    .from('projects')
    .select('id, name, project_number, status, created_at')
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map((r: any) => ({
    id: r.id,
    name: r.name ?? '',
    projectNumber: r.project_number ?? '',
    status: r.status ?? '',
    createdAt: r.created_at ?? '',
  }));
};

// ─── Auftragsannahme (Phase 5) ───────────────────────────────────────────────
// Angenommenes Angebot -> Auftrag (order_number) + automatisch angelegtes Projekt.
// Das Projekt wird bewusst ueber saveProjectToCloud() geschrieben (NIE per direktem
// projects-Insert): nur so werden die relationalen Kind-Tabellen
// (project_services = LV) korrekt mitgepflegt.

/**
 * Naechste freie Projektnummer nach dem Schema des ProjectSetupWizard
 * (JJMM + Typziffer + 3-stellige laufende Nummer, z. B. "26071023").
 *
 * Bewusste Duplikation der Wizard-Logik (ProjectSetupWizard.tsx
 * generateProjectNumber, ~Zeile 192): der Wizard kapselt die Berechnung in
 * React-State und ist von der Service-Schicht nicht wiederverwendbar. Die
 * Verfuegbarkeitspruefung (Race-Absicherung) wird hier ebenfalls gespiegelt.
 * Typziffer '1' = SiGeKo (Default), '2' = SiFa, '3' = sonstige.
 */
export const nextProjectNumber = async (typeDigit = '1'): Promise<string> => {
  const now = new Date();
  const datePart = now.toISOString().slice(2, 4) + String(now.getMonth() + 1).padStart(2, '0');
  const baseStr = `${datePart}${typeDigit}`;

  const { data } = await supabase
    .from('projects')
    .select('project_number')
    .ilike('project_number', `${baseStr}%`)
    .order('project_number', { ascending: false })
    .limit(1);

  let nextSeq = 1;
  if (data && data.length > 0 && data[0].project_number) {
    const lastSeq = parseInt(String(data[0].project_number).replace(baseStr, ''), 10);
    if (!isNaN(lastSeq)) nextSeq = lastSeq + 1;
  }

  let candidate = `${baseStr}${String(nextSeq).padStart(3, '0')}`;
  // Verfuegbarkeitspruefung wie im Wizard: bei Kollision hochzaehlen (begrenzt).
  for (let i = 0; i < 50; i++) {
    const { data: taken } = await supabase
      .from('projects')
      .select('id')
      .eq('project_number', candidate)
      .maybeSingle();
    if (!taken) break;
    nextSeq++;
    candidate = `${baseStr}${String(nextSeq).padStart(3, '0')}`;
  }
  return candidate;
};

/** Belegtpruefung fuer die im Dialog editierbare Projektnummer. */
export const isProjectNumberTaken = async (projectNumber: string): Promise<boolean> => {
  const { data } = await supabase
    .from('projects')
    .select('id')
    .eq('project_number', projectNumber)
    .maybeSingle();
  return !!data;
};

/**
 * Stand des Angebots nach einer GESCHEITERTEN Annahme, die schon geschrieben
 * hatte (Claim, Kompensation, c5). Haengt am geworfenen Fehler, damit der
 * Editor seinen Anker nachziehen kann — sonst endete der naechste Versuch
 * bzw. das naechste Speichern in einem selbst verursachten Konflikt.
 */
export interface QuoteAcceptFailureState {
  /** updated_at nach dem letzten bekannten Schreiben. */
  updatedAt: string;
  /** Die Zeile nach dem letzten Schreiben, falls sie gelesen wurde. */
  quote: Quote | null;
  /** Steht das Angebot danach auf "angenommen" (Projekt existiert / Ruecksetzen scheiterte)? */
  accepted: boolean;
  /** Der Status in dem Fall: 'accepted' oder — bei Teilbeauftragung — 'partial'. */
  status?: Quote['status'];
  /**
   * Annahme als Rahmenvertrag: der Kunde, wenn der Vertrag schon bei ihm
   * steht — der Editor zieht damit die Kundenliste nach.
   */
  customer?: Customer;
}

export const attachAcceptState = (err: unknown, state: QuoteAcceptFailureState): unknown => {
  const target = err instanceof Error ? err : new Error(String(err));
  Object.assign(target, { acceptState: state });
  return target;
};

/** Der am Annahme-Fehler haengende Angebotsstand, falls vorhanden. */
export const acceptFailureState = (err: unknown): QuoteAcceptFailureState | null => {
  if (!err || typeof err !== 'object' || !('acceptState' in err)) return null;
  const state = (err as { acceptState?: QuoteAcceptFailureState }).acceptState;
  return state && typeof state.updatedAt === 'string' ? state : null;
};

/**
 * Projekt-Kickoff nach der Angebotsannahme (docs/projekt-kickoff-plan.md
 * §4.4): Mail mit Kalenderdatei an die Zuständigen des neuen Projekts.
 *
 * Fire-and-forget: die Annahme ist zu diesem Zeitpunkt fertig und darf weder
 * auf den Versand warten noch an ihm scheitern. Die Quittungszeile legt
 * ohnehin der DB-Trigger an — scheitert die Mail, bleibt das Popup als Weg.
 *
 * Dynamischer Import: kickoffService zieht Mailer und cloudService nach.
 * Statisch gäbe das einen weiteren Pfad crmService → … → cloudService, und
 * die Annahme-Tests müssten den ganzen Mailer mitladen.
 */
const kickoffNachAnnahme = (projectId: string, erstellerName?: string): void => {
  void import('./kickoff/kickoffService')
    .then(({ benachrichtigeKickoff }) =>
      benachrichtigeKickoff([projectId], erstellerName ? { erstellerName } : undefined))
    .catch((err) => console.warn('[crmService] Kickoff-Mail nach Annahme nicht ausgelöst:', err));
};

/**
 * Profile der Verantwortlichen Planungsphase für die Angebotsannahme
 * (docs/projekt-kickoff-plan.md §5) — in Auswahlreihenfolge, ohne den
 * Hauptverantwortlichen und ohne Dubletten (dieselben Regeln wie im
 * Assistenten, services/planungsZuweisung.ts).
 *
 * Frisch aus `profiles` statt aus dem Dialog: Name, E-Mail und Telefon landen
 * in der Vertreterliste und in der Kickoff-Mail, und acceptQuote soll mit
 * bloßen ids aufrufbar bleiben.
 *
 * Wirft NIE: Die Planungszuweisung ist ein Zusatz. Scheitert das Laden oder
 * fehlt ein Profil, wird die Person ausgelassen und gewarnt — die Annahme
 * selbst (Auftragsnummer, Projekt, Angebotsstatus) läuft weiter. Ein Wurf hier
 * stünde mitten im try von acceptQuote und setzte die Annahme zurück.
 */
const ladePlanungsPersonen = async (
  ids: string[] | undefined,
  besitzerId: string,
): Promise<UserProfile[]> => {
  const gesucht = [...new Set((ids || []).map((id) => (id || '').trim()))]
    .filter((id) => !!id && id !== besitzerId);
  if (gesucht.length === 0) return [];
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email, phone')
      .in('id', gesucht);
    if (error) throw error;
    const personen = planungsPersonenAusIds(gesucht, (data || []) as UserProfile[], besitzerId);
    const fehlend = gesucht.filter((id) => !personen.some((p) => p.id === id));
    if (fehlend.length > 0) {
      console.warn('[crmService] Planungsperson(en) ohne Profil — ausgelassen:', fehlend);
    }
    return personen;
  } catch (err) {
    console.warn('[crmService] Profile der Planungspersonen nicht ladbar — Annahme ohne Planungszuweisung:', err);
    return [];
  }
};

export const acceptQuote = async (
  quote: Quote,
  customer: Customer,
  coordinatorProfile: any,
  settings: any,
  overrides?: AcceptQuoteValues
): Promise<{ projectId: string; orderNumber: string; projectRow: any; quote: Quote }> => {
  // a) Statusuebergang absichern (draft/sent -> accepted).
  if (!canTransition(quote.status, 'accepted')) {
    throw new Error(`Angebot kann im Status "${quote.status}" nicht angenommen werden.`);
  }

  // a2) Koordinator-Profil zuerst validieren – ohne ist keine Zuordnung moeglich.
  if (!coordinatorProfile?.id) {
    throw new Error('Kein Koordinator-Profil verfügbar – bitte neu anmelden.');
  }

  // a3) Umfang der Beauftragung (Teilbeauftragung). Ohne Angabe aus dem Dialog
  //     wie bisher VOLLSTAENDIG: alle offenen Mengen. Daraus entsteht Teil 1
  //     (quotes.order_tranches) — er traegt Bestellnummer, Datum und den
  //     eingefrorenen Betrag. Ein Angebot ohne beauftragbare Menge (nur NEP,
  //     alles gestrichen, Mengen 0) liess sich bisher annehmen und bleibt so:
  //     dann gibt es keinen Teil.
  const nowIso = new Date().toISOString();
  const fullLines = openOrderLines(quote);
  let tranche = null as ReturnType<typeof buildTranche> | null;
  if (fullLines.length > 0) {
    const lines = overrides?.orderLines ? normalizeOrderLines(overrides.orderLines) : fullLines;
    const lineErrors = validateOrderLines(quote, lines);
    if (lineErrors.length) throw new Error(`Umfang der Beauftragung: ${lineErrors.join(' ')}`);
    tranche = buildTranche(quote, {
      id: crypto.randomUUID(),
      at: nowIso,
      kind: 'order',
      lines,
      orderDate: overrides?.orderDate,
      by: overrides?.actor?.id,
      byName: overrides?.actor?.name || coordinatorProfile.name || '',
      customerRef: overrides?.customerRef,
    });
  }
  const targetStatus: Quote['status'] = tranche ? statusAfterLines(quote, tranche.lines) : 'accepted';

  // b) Atomarer Status-Claim VOR jeder Anlage: verhindert Doppelklick/Parallelannahme.
  //    Nur genau ein Aufruf gewinnt den Uebergang draft/sent -> accepted.
  //    Zusaetzlich an den Stand gebunden, den der Annehmende SIEHT
  //    (updated_at): hat inzwischen jemand Texte/Positionen gespeichert,
  //    schriebe c5 unten dessen Stand mit dem alten ueber — und das Projekt
  //    entstuende aus einem Angebot, das so nicht mehr gilt.
  //    Der Auftragsteil geht MIT dem Claim in die Zeile (noch ohne appliedAt):
  //    scheitert danach etwas, nimmt die Kompensation ihn mit zurueck.
  const anchor = typeof quote.updatedAt === 'string' && quote.updatedAt.trim() ? quote.updatedAt : null;
  const runClaim = (withTranche: boolean) => {
    let q = supabase
      .from('quotes')
      .update({
        status: withTranche ? targetStatus : 'accepted',
        accepted_at: nowIso,
        ...(withTranche && tranche ? { order_tranches: [tranche] } : {}),
      })
      .eq('id', quote.id)
      .in('status', ['draft', 'sent']);
    if (anchor) q = q.eq('updated_at', anchor);
    return q.select('id, details, updated_at');
  };
  let wroteTranche = !!tranche;
  let claimResult = await runClaim(wroteTranche);
  if (claimResult.error && wroteTranche && isMissingOrderTranchesSchema(claimResult.error)) {
    // Migration Nr. 140 fehlt. Die VOLLE Annahme geht weiter wie bisher (ohne
    // Auftragsteil, gilt als Altbestand = voll beauftragt); eine Teilbeauftragung
    // geht ohne die Spalte nicht — nichts ist geschrieben.
    if (targetStatus !== 'accepted') throw new Error(MISSING_ORDER_TRANCHES_MESSAGE);
    console.warn('[crmService] quotes.order_tranches fehlt (Migration Nr. 140) — Annahme ohne Auftragsteil.');
    wroteTranche = false;
    claimResult = await runClaim(false);
  }
  const { data: claimed, error: claimErr } = claimResult;
  if (claimErr) throw claimErr;
  if (!claimed?.length) {
    if (anchor) {
      const { data: current, error: readErr } = await supabase
        .from('quotes').select('*, customers(name)').eq('id', quote.id).maybeSingle();
      if (readErr) throw readErr;
      if (!current) throw new QuoteNotSavedError();
      // Noch offen, aber ein anderer Stand: VOR jeder Anlage abbrechen, der
      // Editor laedt/fuehrt zusammen und der Nutzer nimmt erneut an.
      if (current.status === 'draft' || current.status === 'sent') {
        throw new QuoteConflictError(mapQuoteRow(current));
      }
    }
    throw new Error('Angebot wurde bereits verarbeitet.');
  }
  const claimedUpdatedAt = String(claimed[0].updated_at);
  // Letzter bekannter Stand der Zeile — fuer den Anker des Editors.
  let lastUpdatedAt = claimedUpdatedAt;
  let lastRow: Quote | null = null;

  // Ab hier steht fest, ob die Projektzeile schon existiert. Danach darf die
  // Kompensation den Status NICHT mehr zuruecksetzen: das Projekt bleibt ja
  // bestehen, und ein zweiter Annahmeversuch legte ein Geisterprojekt daneben.
  let createdProjectId: string | null = null;

  try {
    // c) Auftragsnummer (Kind 'AB') ziehen – gleiche RPC wie createQuote (AN).
    //    'AB' laeuft weiterhin ueber den globalen Zaehler, die Typziffer ist
    //    dort ohne Wirkung.
    const orderNumber = await nextDocumentNumber('AB');

    // c2) Projektnummer nach Wizard-Schema. Typziffer aus der Angebotsnummer
    //     (A-JJMMTNNN), damit Angebot und Projekt denselben Typ tragen; bei
    //     Altnummern wie bisher SiGeKo ('1').
    //     Nummer aus dem Dialog hat Vorrang; ohne Dialog wie bisher automatisch.
    //     Ist ein Projekttyp gewaehlt, folgt die Typziffer ihm (wie im
    //     Anlage-Assistenten) — nicht der Angebotsnummer.
    const projectNumber =
      overrides?.projectNumber ||
      (await nextProjectNumber(
        overrides?.projectType
          ? typzifferFuerProjektTyp(overrides.projectType)
          : typeDigitFromDocumentNumber(quote.quoteNumber) ?? '1',
      ));

    // c3) Report aus Angebot ableiten. orderNumber wird mitgegeben, damit die
    //     Systemnotiz in quoteToReport die Auftragsnummer enthaelt.
    //     Teilbeauftragung: ins Projekt-LV kommen nur die beauftragten Mengen
    //     (NEP wie bisher mit). Das ANGEBOT behaelt alle Positionen (c5).
    const report = quoteToReport(
      { ...quote, orderNumber, items: tranche ? projectItemsForLines(quote.items || [], tranche.lines) : quote.items },
      customer, coordinatorProfile, projectNumber, settings, overrides
    );
    //     Die Systemnotiz traegt die id des Auftragsteils. Sie haelt den Teil
    //     im Projekt fest (auch fuer Koordinatoren — quotes ist admin-only)
    //     und macht das Eintragen wiederholbar: ein Teil, dessen Notiz im
    //     Projekt steht, ist dort angekommen (quoteOrderService).
    if (tranche && wroteTranche) {
      const notes = report.metadata.globalData?.notes || [];
      if (notes[0]) {
        const partial = targetStatus === 'partial';
        notes[0] = {
          ...notes[0],
          id: tranche.id,
          text: partial || tranche.customerRef
            ? `${notes[0].text} ${trancheNoteText(quote, tranche)}`
            : notes[0].text,
        };
      }
    }

    // c3b) Verantwortliche Planungsphase in die Vertreterliste — VOR dem
    //      Speichern, genau wie App.handleSetupComplete: der DB-Trigger
    //      project_kickoff_nach_anlage legt beim Insert aus dieser Liste je
    //      Planungsperson eine Kalender-Quittung an, und über ihre id erhalten
    //      sie Projektzugriff (representatives_access.sql). Position 0 bleibt
    //      der Hauptverantwortliche aus quoteToReport (Besitzer, Ausführung);
    //      applyRepresentatives spiegelt nur ihn in die Altfelder.
    //      Ohne Planungspersonen bleibt der Report unverändert.
    //      SiFa-Betreuung hat keine Planungsphase — nie Planungspersonen, wie
    //      im Assistenten (planungsPersonenAusAuswahl, isSifa).
    const planungsPersonen = overrides?.projectType === 'sifa'
      ? []
      : await ladePlanungsPersonen(overrides?.planningUserIds, coordinatorProfile.id);
    if (planungsPersonen.length > 0) {
      const meta = report.metadata;
      report.metadata = applyRepresentatives(meta, [
        { id: coordinatorProfile.id, name: meta.coordinator || '', email: meta.coordinatorEmail, phone: meta.coordinatorPhone },
        ...planungsEintraege(planungsPersonen),
      ]);
    }

    // c4) Projekt anlegen – ueber saveProjectToCloud (Sync-Anker + LV-Kindtabellen).
    const projectRow = await saveProjectToCloud(report, undefined, coordinatorProfile.id);
    if (!projectRow?.id) throw new Error('Projekt konnte nicht angelegt werden.');
    createdProjectId = projectRow.id;

    // c5) Auftragsnummer/Projektbezug am bereits geclaimten Angebot nachtragen.
    //     Dabei die Endsummen NEU aus den uebergebenen Positionen rechnen: der
    //     Editor speichert entprellt, eine Annahme direkt nach der letzten
    //     Preisaenderung fror sonst die veraltete net_total/gross_total der
    //     Zeile ein — das Projekt trug den neuen Wert, die Angebots-Statistik
    //     den alten.
    //     Aus demselben Grund wird der GESAMTE editierbare Stand geschrieben
    //     (Titel, Texte, details, Rabatt, Gueltigkeit, nummerierte Positionen),
    //     nicht nur Positionen und Summen: in der Datenbank muss genau das
    //     Angebot stehen, das angenommen wurde. Der Nachfass-Verlauf kommt
    //     vom Server (Claim-Antwort) — der Editor kann ihn nicht aendern.
    const sections = quote.details?.sections ?? DEFAULT_QUOTE_SECTIONS;
    const acceptedItems = computePosNumbers(sections, quote.items || []);
    const totals = calculateQuoteTotals(
      acceptedItems,
      quote.discountPercent || 0,
      quote.taxRate || 0,
    );
    const serverDetails = (claimed[0]?.details ?? undefined) as QuoteDetails | undefined;
    const followUpLog = unionFollowUpLog(serverDetails?.followUpLog, quote.details?.followUpLog);
    const acceptedDetails: QuoteDetails = {
      ...(quote.details || {}),
      sections,
      ...(followUpLog.length > 0 ? { followUpLog } : {}),
    };
    const acceptedRow = toQuoteRow({
      title: quote.title ?? '',
      quoteDate: quote.quoteDate,
      validUntil: quote.validUntil,
      introText: quote.introText,
      outroText: quote.outroText,
      paymentTerms: quote.paymentTerms,
      discountPercent: quote.discountPercent || 0,
      taxRate: quote.taxRate || 0,
      items: acceptedItems,
      details: acceptedDetails,
      netTotal: totals.netTotal,
      grossTotal: totals.grossTotal,
    });
    //     Verankert auf dem Stand des Claims: speichert dazwischen noch
    //     jemand (anderer Tab), wird dessen Stand NICHT ueberschrieben — dann
    //     wird nur der Projektbezug nachgetragen, damit das Projekt nicht
    //     ohne Angebot dasteht.
    //     Teil 1 steht jetzt im Projekt-LV: appliedAt setzen.
    const appliedAt = new Date().toISOString();
    const { data: accRows, error: updErr } = await supabase
      .from('quotes')
      .update({
        ...acceptedRow,
        order_number: orderNumber,
        project_id: projectRow.id,
        ...(tranche && wroteTranche ? { order_tranches: markTrancheApplied([tranche], tranche.id, appliedAt) } : {}),
        updated_at: new Date().toISOString(),
      })
      .eq('id', quote.id)
      .eq('updated_at', claimedUpdatedAt)
      .select('*, customers(name)');
    if (updErr) throw updErr;
    if (accRows?.length) {
      lastRow = mapQuoteRow(accRows[0]);
      lastUpdatedAt = lastRow.updatedAt;
    } else {
      console.warn(`[crmService] Angebot ${quote.quoteNumber} wurde waehrend der Annahme gespeichert; `
        + 'nur Auftragsnummer/Projektbezug nachgetragen.');
      // Frisch lesen und VERANKERT schreiben: die Auftragsteile vom Server
      // nehmen und NUR appliedAt dieses Teils setzen. Unverankert ginge ein
      // inzwischen erfasster Teil (z. B. „Rest entfaellt" aus einem anderen
      // Tab) verloren. Drei Versuche, dann Fehler (Projekt steht — der Fehler
      // unten sagt, was zu pruefen ist).
      let linked: Record<string, unknown> | null = null;
      for (let attempt = 0; attempt < 3 && !linked; attempt += 1) {
        const { data: cur, error: curErr } = await supabase
          .from('quotes').select('*').eq('id', quote.id).maybeSingle();
        if (curErr) throw curErr;
        if (!cur) throw new QuoteNotSavedError('Angebotsbezug konnte nicht gespeichert werden (Angebot nicht gefunden).');
        const appliedTranches = tranche && wroteTranche
          ? markTrancheApplied(Array.isArray(cur.order_tranches) ? cur.order_tranches : [tranche], tranche.id, appliedAt)
          : undefined;
        const { data: linkRows, error: linkErr } = await supabase
          .from('quotes')
          .update({
            order_number: orderNumber,
            project_id: projectRow.id,
            ...(appliedTranches !== undefined ? { order_tranches: appliedTranches } : {}),
            updated_at: new Date().toISOString(),
          })
          .eq('id', quote.id)
          .eq('updated_at', cur.updated_at)
          .select('*, customers(name)');
        if (linkErr) throw linkErr;
        if (linkRows?.length) linked = linkRows[0];
      }
      if (!linked) throw new QuoteNotSavedError('Angebotsbezug konnte nicht gespeichert werden (Angebot wird parallel geändert).');
      // Der Stand des ANDEREN bleibt — der Editor muss genau ihn uebernehmen.
      lastRow = mapQuoteRow(linked);
      lastUpdatedAt = lastRow.updatedAt;
    }
    const finalQuote: Quote = lastRow;

    // c6) Projekt dem Kunden zuordnen (setzt nur customer_id, kein Sync-Anker-Touch).
    await linkProjectsToCustomer(quote.customerId, [projectRow.id]);

    // c6b) Auftragswert fixieren (ERP/CRM-Plan Abschnitt 8,
    //      „Auftragswert-Feststellung"). ADDITIV: der Wert kommt aus dem
    //      Dialog (Vorschlag = Angebots-Netto, editierbar) und wird BEST
    //      EFFORT geschrieben — fehlt die Migration Nr. 23 oder greift die
    //      RLS, darf das die bereits angelegte Projektzeile nicht kippen.
    //      Nachtragen geht im ERP-Cockpit unter „Auftragswerte".
    //      0 bzw. leer heisst bewusst „nicht fixieren".
    if (overrides?.orderValueCents && overrides.orderValueCents > 0) {
      await fixiereAuftragswertBestEffort({
        projectId: projectRow.id,
        orderValueCents: overrides.orderValueCents,
        quoteId: quote.id,
        note: targetStatus === 'partial'
          ? `Teilbeauftragung Teil 1 von ${quote.quoteNumber} fixiert.`
          : `Bei Annahme von ${quote.quoteNumber} fixiert.`,
      });
    }

    // c7) Fremdzuweisung: der Kollege erfaehrt sonst nichts von seinem Projekt.
    //     Nicht-fatal — ein Benachrichtigungsfehler darf den Auftrag nicht kippen.
    //     Absender ist, wer annimmt (actor = angemeldeter Nutzer): die RLS
    //     notifications_insert_as_self verlangt sender_id = auth.uid(). Mit
    //     quote.createdBy lehnte die Datenbank die Zeile STILL ab, sobald ein
    //     Kollege das Angebot angelegt hatte — createNotificationsForMentions
    //     prueft den Fehler nicht, die Mitteilung fehlte einfach. Ohne actor
    //     (Aufruf ohne Dialog) bleibt es beim bisherigen Absender.
    //     Name: der des Annehmenden. coordinatorProfile ist meist ein Profil
    //     ohne `name` (nur first_/last_name) — der bisherige Wert war deshalb
    //     leer; dann 'System' wie in App.handleSetupComplete.
    const absenderId = overrides?.actor?.id || quote.createdBy;
    const absenderName = (overrides?.actor?.name || '').trim() || String(coordinatorProfile.name || '').trim() || 'System';
    //     Verglichen wird mit dem ABSENDER, nicht mit dem Angebotsersteller:
    //     nimmt B ein Angebot von A an und macht A zum Hauptverantwortlichen,
    //     muss A die Mitteilung bekommen (Codex-Review 12.10.).
    if (overrides?.coordinatorId && overrides.coordinatorId !== absenderId) {
      await createNotificationsForMentions(
        [overrides.coordinatorId],
        crypto.randomUUID(),
        `Neues Projekt zugewiesen: ${report.metadata.projectName}`,
        projectRow.id,
        report.metadata.projectName,
        absenderId,
        absenderName,
      ).catch((err) => console.warn('Zuweisungs-Benachrichtigung fehlgeschlagen:', err));
    }
    //     Die Planungspersonen erfahren es auf demselben Weg und vom selben
    //     Absender — jede bekommt ihre eigene Glocken-Mitteilung (wie
    //     App.handleSetupComplete).
    if (planungsPersonen.length > 0) {
      await createNotificationsForMentions(
        planungsPersonen.map((p) => p.id),
        crypto.randomUUID(),
        `Neues Projekt zugewiesen (Planung): ${report.metadata.projectName}`,
        projectRow.id,
        report.metadata.projectName,
        absenderId,
        absenderName,
      ).catch((err) => console.warn('Planungs-Benachrichtigung fehlgeschlagen:', err));
    }

    // c8) Projekt-Kickoff: Termine per Mail an Besitzer (und Planungsperson).
    //     Letzter Schritt vor dem return — danach kann nichts mehr werfen, der
    //     catch unten löst ihn also nie ein zweites Mal aus.
    kickoffNachAnnahme(projectRow.id, overrides?.actor?.name);

    return { projectId: projectRow.id, orderNumber, projectRow, quote: finalQuote };
  } catch (err) {
    // d) Kompensation, aber nur SOLANGE noch kein Projekt existiert: dann ist
    //    nichts entstanden und ein erneuter Versuch ist gefahrlos.
    if (!createdProjectId) {
      // Auch das Ruecksetzen stempelt updated_at — der neue Anker haengt am
      // Fehler (acceptFailureState), sonst liefe der naechste Versuch in
      // einen selbst verursachten Konflikt.
      let state: QuoteAcceptFailureState = { updatedAt: lastUpdatedAt, quote: lastRow, accepted: true, status: targetStatus };
      try {
        // Der mit dem Claim geschriebene Auftragsteil geht mit zurueck.
        const { data: reverted, error: revErr } = await supabase
          .from('quotes')
          .update({ status: quote.status, accepted_at: null, ...(wroteTranche ? { order_tranches: [] } : {}) })
          .eq('id', quote.id)
          .select('*, customers(name)');
        if (!revErr && reverted?.length) {
          const row = mapQuoteRow(reverted[0]);
          state = { updatedAt: row.updatedAt, quote: row, accepted: false };
        }
      } catch {
        /* Revert best-effort: Fehler hier bewusst ignorieren. */
      }
      throw attachAcceptState(err, state);
    }

    // Das Projekt steht bereits. Den Status zurueckzusetzen hiesse: das
    // Angebot sieht wieder offen aus, der naechste Annahmeversuch legte ein
    // zweites Projekt an — und das erste bliebe als Geisterprojekt ohne
    // Angebotsbezug liegen. Also bleibt "angenommen" stehen, und der Fehler
    // nennt die Projektnummer, damit der Rest von Hand nachgetragen werden kann.
    // Der Koordinator bekommt seine Termine trotzdem: das Projekt ist ihm
    // zugewiesen, nur der Angebotsbezug hakt — sonst ginge es genau hier unter.
    kickoffNachAnnahme(createdProjectId, overrides?.actor?.name);
    throw attachAcceptState(new Error(
      `Das Projekt wurde angelegt (ID ${createdProjectId}), aber der Angebotsbezug konnte nicht `
      + `vollstaendig geschrieben werden: ${(err as Error)?.message || String(err)}. `
      + 'Das Angebot bleibt angenommen — bitte Auftragsnummer/Projektzuordnung im Projekt prüfen, '
      + 'NICHT erneut annehmen (sonst entsteht ein zweites Projekt).',
    ), { updatedAt: lastUpdatedAt, quote: lastRow, accepted: true, status: targetStatus });
  }
};

/**
 * Traegt am Angebot nach, dass es als Rahmenvertrag angenommen wurde:
 * Auftragsnummer und details.frameworkContractId — verankert auf dem frisch
 * gelesenen Stand, die uebrigen details bleiben, wie sie sind.
 *
 * Zwei Aufrufer: die Annahme (hat zwischen Claim und Schreiben jemand
 * gespeichert) und die Reparatur im Editor, wenn eine Annahme NACH dem
 * Anlegen des Vertrags abbrach — sonst stuende das Angebot als gewoehnlich
 * angenommen da, und ein zweiter Annahmeversuch scheitert am Status.
 * Idempotent; drei Versuche, dann QuoteNotSavedError.
 */
export const linkQuoteToFrameworkContract = async (
  quoteId: string,
  contract: FrameworkContract,
  orderNumber?: string,
): Promise<Quote> => {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const { data: cur, error: curErr } = await supabase
      .from('quotes').select('*').eq('id', quoteId).maybeSingle();
    if (curErr) throw curErr;
    if (!cur) throw new QuoteNotSavedError('Angebotsbezug konnte nicht gespeichert werden (Angebot nicht gefunden).');
    // Ein zurueckgesetztes (wieder offenes) Angebot darf keinen Vertragsbezug bekommen.
    if (cur.status !== 'accepted') {
      throw new Error(`Angebot steht auf "${cur.status}" — der Rahmenvertragsbezug wird nur an angenommenen Angeboten nachgetragen.`);
    }
    const curDetails = (cur.details ?? {}) as QuoteDetails;
    let link = supabase
      .from('quotes')
      .update({
        order_number: orderNumber || contract.source?.orderNumber || cur.order_number || null,
        details: { ...curDetails, frameworkContractId: contract.id },
        updated_at: new Date().toISOString(),
      })
      .eq('id', quoteId);
    // Zeile ohne updated_at: NULL trifft kein eq, nur is.
    link = cur.updated_at == null ? link.is('updated_at', null) : link.eq('updated_at', cur.updated_at);
    const { data: linkRows, error: linkErr } = await link.select('*, customers(name)');
    if (linkErr) throw linkErr;
    if (linkRows?.length) return mapQuoteRow(linkRows[0]);
  }
  throw new QuoteNotSavedError('Angebotsbezug konnte nicht gespeichert werden (Angebot wird parallel geändert).');
};

/**
 * Annahme eines Angebots OHNE Endsumme (totalsMode 'unitPrices') als
 * Rahmenvertrag: es entsteht KEIN Projekt (kein Bauvorhaben, nur Preise),
 * sondern ein FrameworkContract am Kunden (customers.framework_contracts).
 * Gleiche Absicherung wie acceptQuote — Claim, Anker, Kompensation —, aber
 * ohne Projekt, ohne Auftragsteile und ohne Auftragswert.
 */
export const acceptQuoteAsFrameworkContract = async (
  quote: Quote,
  customer: Customer,
  values: AcceptFrameworkValues,
): Promise<{ orderNumber: string; contract: FrameworkContract; customer: Customer; quote: Quote }> => {
  // a) Vorpruefung — alles, was ohne Schreiben feststeht, VOR dem Claim.
  if (!canTransition(quote.status, 'accepted')) {
    throw new Error(`Angebot kann im Status "${quote.status}" nicht angenommen werden.`);
  }
  if (!isUnitPriceQuote(quote)) {
    throw new Error('Nur Angebote ohne Endsumme (nur Einheitspreise) können als Rahmenvertrag angenommen werden.');
  }
  if (frameworkItemsFromQuote(quote).length === 0) {
    throw new Error('Das Angebot enthält keine Preispositionen — daraus entsteht kein Rahmenvertrag.');
  }
  // Der Vertrag haengt am Kunden, nicht am Angebot: ein falscher Kunde aus dem
  // Dialog legte die Preise still beim Falschen ab.
  if (!customer?.id || (quote.customerId && customer.id !== quote.customerId)) {
    throw new Error('Der Kunde passt nicht zum Angebot — Rahmenvertrag wird nicht angelegt.');
  }

  // b) Atomarer Status-Claim wie bei acceptQuote: nur ein Aufruf gewinnt
  //    draft/sent -> accepted, und nur auf dem Stand, den der Annehmende sieht.
  const nowIso = new Date().toISOString();
  const anchor = typeof quote.updatedAt === 'string' && quote.updatedAt.trim() ? quote.updatedAt : null;
  let claimQuery = supabase
    .from('quotes')
    .update({ status: 'accepted', accepted_at: nowIso })
    .eq('id', quote.id)
    .in('status', ['draft', 'sent']);
  if (anchor) claimQuery = claimQuery.eq('updated_at', anchor);
  const { data: claimed, error: claimErr } = await claimQuery.select('id, details, updated_at');
  if (claimErr) throw claimErr;
  if (!claimed?.length) {
    if (anchor) {
      const { data: current, error: readErr } = await supabase
        .from('quotes').select('*, customers(name)').eq('id', quote.id).maybeSingle();
      if (readErr) throw readErr;
      if (!current) throw new QuoteNotSavedError();
      if (current.status === 'draft' || current.status === 'sent') {
        throw new QuoteConflictError(mapQuoteRow(current));
      }
    }
    throw new Error('Angebot wurde bereits verarbeitet.');
  }
  const claimedUpdatedAt = String(claimed[0].updated_at);
  let lastUpdatedAt = claimedUpdatedAt;
  let lastRow: Quote | null = null;

  // Steht der Vertrag beim Kunden, darf die Kompensation den Status NICHT
  // mehr zuruecksetzen: das Angebot saehe wieder offen aus, obwohl seine
  // Preise schon beim Kunden gelten.
  let contractInPlace: FrameworkContract | null = null;
  // Kundenstand nach dem Anhaengen — geht auch an einen spaeteren Fehler mit.
  let finalCustomer: Customer | null = null;

  try {
    // c) Auftragsnummer (Kind 'AB') — wie acceptQuote.
    const orderNumber = await nextDocumentNumber('AB');

    // d) Vertrag bauen (rein).
    const built = buildFrameworkContractFromQuote(quote, values, {
      id: crypto.randomUUID(),
      acceptedAt: nowIso,
      orderNumber,
    });

    // e) An den Kunden haengen — VERANKERT auf updated_at der frisch gelesenen
    //    Zeile: framework_contracts ist EIN jsonb, ein unverankertes Schreiben
    //    naehme eine parallele Aenderung am Kunden (anderer Vertrag,
    //    Standard-Kennzeichen) wieder zurueck. Idempotent je Angebot: steht
    //    der Vertrag aus diesem Angebot schon dort (Wiederholung nach einem
    //    Abbruch), gilt der vorhandene und es wird nichts geschrieben.
    for (let attempt = 0; attempt < 3 && !contractInPlace; attempt += 1) {
      const { data: row, error: readErr } = await supabase
        .from('customers').select('*').eq('id', customer.id).maybeSingle();
      if (readErr) throw readErr;
      if (!row) throw new Error('Kunde nicht gefunden — Rahmenvertrag konnte nicht hinterlegt werden.');
      const current = mapCustomerRow(row);
      const next = addFrameworkContract(current.frameworkContracts ?? [], built);
      if (!next.added) {
        contractInPlace = next.contract;
        finalCustomer = current;
        break;
      }
      let write = supabase
        .from('customers')
        .update({ framework_contracts: next.list, updated_at: new Date().toISOString() })
        .eq('id', customer.id);
      // Altbestand ohne updated_at: NULL trifft kein eq, nur is.
      write = row.updated_at == null ? write.is('updated_at', null) : write.eq('updated_at', row.updated_at);
      const { data: written, error: writeErr } = await write.select('*');
      if (writeErr) throw writeErr;
      if (written?.length) {
        contractInPlace = next.contract;
        finalCustomer = mapCustomerRow(written[0]);
      }
    }
    if (!contractInPlace || !finalCustomer) {
      throw new Error('Rahmenvertrag konnte nicht beim Kunden hinterlegt werden (Kunde wird parallel geändert). Bitte erneut annehmen.');
    }
    const contract: FrameworkContract = contractInPlace;
    // Bei einer Wiederholung traegt der vorhandene Vertrag die Auftragsnummer
    // des ersten Versuchs — Angebot und Vertrag muessen dieselbe nennen.
    const effectiveOrderNumber = contract.source?.orderNumber || orderNumber;

    // f) Angenommenen Stand am Angebot schreiben — wie c5 in acceptQuote: der
    //    GESAMTE editierbare Stand (in der Datenbank steht genau das Angebot,
    //    das angenommen wurde), Summen neu gerechnet, Nachfass-Verlauf vom
    //    Server. Dazu die id des Vertrags; ein Projekt gibt es nicht.
    const sections = quote.details?.sections ?? DEFAULT_QUOTE_SECTIONS;
    const acceptedItems = computePosNumbers(sections, quote.items || []);
    const totals = calculateQuoteTotals(
      acceptedItems,
      quote.discountPercent || 0,
      quote.taxRate || 0,
    );
    const serverDetails = (claimed[0]?.details ?? undefined) as QuoteDetails | undefined;
    const followUpLog = unionFollowUpLog(serverDetails?.followUpLog, quote.details?.followUpLog);
    const acceptedDetails: QuoteDetails = {
      ...(quote.details || {}),
      sections,
      ...(followUpLog.length > 0 ? { followUpLog } : {}),
      frameworkContractId: contract.id,
    };
    const acceptedRow = toQuoteRow({
      title: quote.title ?? '',
      quoteDate: quote.quoteDate,
      validUntil: quote.validUntil,
      introText: quote.introText,
      outroText: quote.outroText,
      paymentTerms: quote.paymentTerms,
      discountPercent: quote.discountPercent || 0,
      taxRate: quote.taxRate || 0,
      items: acceptedItems,
      details: acceptedDetails,
      netTotal: totals.netTotal,
      grossTotal: totals.grossTotal,
    });
    //    Verankert auf dem Claim: hat dazwischen jemand gespeichert, bleibt
    //    dessen Stand — dann wird nur der Vertragsbezug nachgetragen.
    const { data: accRows, error: updErr } = await supabase
      .from('quotes')
      .update({ ...acceptedRow, order_number: effectiveOrderNumber, updated_at: new Date().toISOString() })
      .eq('id', quote.id)
      .eq('updated_at', claimedUpdatedAt)
      .select('*, customers(name)');
    if (updErr) throw updErr;
    if (accRows?.length) {
      lastRow = mapQuoteRow(accRows[0]);
      lastUpdatedAt = lastRow.updatedAt;
    } else {
      console.warn(`[crmService] Angebot ${quote.quoteNumber} wurde waehrend der Annahme gespeichert; `
        + 'nur Auftragsnummer/Rahmenvertrag nachgetragen.');
      // Die uebrigen details gehoeren dem Anderen — nur den Bezug nachtragen.
      lastRow = await linkQuoteToFrameworkContract(quote.id, contract, effectiveOrderNumber);
      lastUpdatedAt = lastRow.updatedAt;
    }

    return { orderNumber: effectiveOrderNumber, contract, customer: finalCustomer, quote: lastRow };
  } catch (err) {
    // Kompensation nur, SOLANGE der Vertrag nicht beim Kunden steht: dann
    // ist nichts entstanden und ein erneuter Versuch ist gefahrlos.
    if (!contractInPlace) {
      let state: QuoteAcceptFailureState = { updatedAt: lastUpdatedAt, quote: lastRow, accepted: true, status: 'accepted' };
      try {
        const { data: reverted, error: revErr } = await supabase
          .from('quotes')
          .update({ status: quote.status, accepted_at: null })
          .eq('id', quote.id)
          .select('*, customers(name)');
        if (!revErr && reverted?.length) {
          const row = mapQuoteRow(reverted[0]);
          state = { updatedAt: row.updatedAt, quote: row, accepted: false };
        }
      } catch {
        /* Revert best-effort: Fehler hier bewusst ignorieren. */
      }
      throw attachAcceptState(err, state);
    }

    // Der Vertrag gilt beim Kunden. Ruecksetzen hiesse: das Angebot sieht
    // offen aus, obwohl seine Preise schon hinterlegt sind. Also bleibt
    // "angenommen" stehen, und der Fehler nennt die Vertragsnummer. Der
    // Editor bekommt den AKTUELLEN Serverstand (der Claim-Anker ist nach
    // einem fremden Speichern veraltet) und den Kunden mit dem Vertrag —
    // daran erkennt er die Annahme und bietet „Bezug nachtragen" an.
    let state: QuoteAcceptFailureState = {
      updatedAt: lastUpdatedAt, quote: lastRow, accepted: true, status: 'accepted',
      ...(finalCustomer ? { customer: finalCustomer } : {}),
    };
    try {
      const { data: cur, error: curErr } = await supabase
        .from('quotes').select('*, customers(name)').eq('id', quote.id).maybeSingle();
      if (!curErr && cur) {
        const row = mapQuoteRow(cur);
        state = { ...state, updatedAt: row.updatedAt, quote: row, status: row.status };
      }
    } catch {
      /* Nachlesen best-effort: dann bleibt der letzte bekannte Stand. */
    }
    throw attachAcceptState(new Error(
      `Der Rahmenvertrag ${contractInPlace.number} wurde beim Kunden hinterlegt, aber der Angebotsbezug `
      + `konnte nicht vollständig geschrieben werden: ${(err as Error)?.message || String(err)}. `
      + 'Das Angebot bleibt angenommen — NICHT erneut annehmen; im Angebot „Bezug nachtragen“ wählen.',
    ), state);
  }
};

// ─── Ausschreibungs-Dokumente (Tender) ──────────────────────────────────────
// Original-Ausschreibungs-PDF, das an ein Angebot gebunden ist. Storage im
// privaten Bucket 'tender-docs', Metadaten in public.tender_documents (siehe
// supabase/tender_documents.sql, admin-only RLS). Abruf ueber signierte URLs.

const TENDER_BUCKET = 'tender-docs';

const mapTenderDocRow = (row: any): TenderDocument => ({
  id: row.id,
  quoteId: row.quote_id,
  kind: row.kind,
  filePath: row.file_path,
  fileName: row.file_name ?? undefined,
  mimeType: row.mime_type ?? undefined,
  uploadedBy: row.uploaded_by ?? undefined,
  createdAt: row.created_at,
});

/**
 * Laedt das Original-Ausschreibungs-PDF in den privaten Bucket und verknuepft
 * es ueber tender_documents mit dem Angebot. Der Storage-Pfad ist eindeutig
 * ({quoteId}/{kind}-{uuid}.{ext}), sodass mehrfaches Hochladen (LV + Anfor-
 * derungen, oder erneuter Versuch) nicht kollidiert.
 */
export const uploadTenderDocument = async (
  quoteId: string,
  kind: TenderDocumentKind,
  file: File,
): Promise<TenderDocument> => {
  const extMatch = /\.([a-z0-9]+)$/i.exec(file.name);
  const ext = (extMatch?.[1] || (file.type === 'application/pdf' ? 'pdf' : 'bin')).toLowerCase();
  const path = `${quoteId}/${kind}-${crypto.randomUUID()}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from(TENDER_BUCKET)
    .upload(path, file, { upsert: false, contentType: file.type || 'application/octet-stream' });
  if (upErr) throw upErr;

  const { data, error } = await supabase
    .from('tender_documents')
    .insert([{
      quote_id: quoteId,
      kind,
      file_path: path,
      file_name: file.name,
      mime_type: file.type || null,
    }])
    .select('*')
    .single();
  if (error) {
    // Verwaistes Storage-Objekt nach fehlgeschlagenem Insert best-effort loeschen.
    await supabase.storage.from(TENDER_BUCKET).remove([path]).then(undefined, () => { /* ignore */ });
    throw error;
  }
  return mapTenderDocRow(data);
};

export const fetchTenderDocuments = async (quoteId: string): Promise<TenderDocument[]> => {
  const { data, error } = await supabase
    .from('tender_documents')
    .select('*')
    .eq('quote_id', quoteId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(mapTenderDocRow);
};

/** Signierte URL (Standard 1 h) zum Anzeigen/Herunterladen eines Tender-PDFs. */
export const getTenderDocumentSignedUrl = async (
  filePath: string,
  expiresInSeconds = 3600,
): Promise<string> => {
  const { data, error } = await supabase.storage
    .from(TENDER_BUCKET)
    .createSignedUrl(filePath, expiresInSeconds);
  if (error) throw error;
  return data?.signedUrl ?? '';
};

export const deleteTenderDocument = async (doc: TenderDocument): Promise<void> => {
  const { error } = await supabase.from('tender_documents').delete().eq('id', doc.id);
  if (error) throw error;
  // Storage-Objekt best-effort entfernen (Row ist bereits weg).
  await supabase.storage.from(TENDER_BUCKET).remove([doc.filePath]).then(undefined, () => { /* ignore */ });
};
